import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, CircularProgress, TextField, Grid, Chip, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox, Autocomplete, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Snackbar, Accordion, AccordionSummary, AccordionDetails, Divider, Stepper, Step, StepLabel, Tooltip, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Delete as DeleteIcon, Science as ScienceIcon, CheckCircle as CheckCircleIcon, AddCircle as AddCircleIcon, Lock as LockIcon, OpenInNew as OpenInNewIcon, ExpandMore as ExpandMoreIcon, Business as BusinessIcon, Store as StoreIcon, Add as AddIcon, Refresh as RefreshIcon, Warning as WarningIcon, Save as SaveIcon, Send as SendIcon, MailOutline as MailOutlineIcon, Close as CloseIcon } from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { FIELD_LIMITS } from '../../constants/fieldLimits';
import ServiceCarousel from './ServiceCarousel';
import AddHazardousSubstanceModal from '../catalogos/AddHazardousSubstanceModal';
import { IVA_RATE_PERCENT, calculateIva, formatMoney, quotationSummaryTotal } from '../../utils/quotationTax';
import { buildPricingInput, buildQuotationLines, restoreQuotationLines } from '../../utils/quotationLines';

// Límites de captura: vienen de constants/fieldLimits.js (mismos valores en todo el sistema).
const LIMITS = {
  comments: FIELD_LIMITS.quotationComments,
  additionalDescription: FIELD_LIMITS.additionalServiceDescription,
  additionalNotes: FIELD_LIMITS.additionalServiceNotes,
  maxPrice: FIELD_LIMITS.maxPrice,
  maxQuantity: FIELD_LIMITS.maxQuantity,
};

// null = el valor no es válido y se ignora la tecla (el campo conserva lo anterior).
const limitNumberInput = (raw, max) => {
  if (raw === '') return '';
  if (!/^\d*\.?\d{0,2}$/.test(raw)) return null;
  const value = raw.replace(/^0+(?=\d)/, '');
  return Number(value) > max ? null : value;
};

const charCounter = (value, max) => `${(value || '').length}/${max}`;

// Pasos visibles al guardar (mismo estilo que el envío). Cada uno avanza cuando termina la
// llamada real correspondiente; el backend guarda cotización + entes + sucursales en una sola.
const SAVE_STEPS = [
  { label: 'Validando la información', percent: 10 },
  { label: 'Preparando servicios y puntos de cobertura', percent: 25 },
  { label: 'Guardando la cotización y actualizando sucursales', percent: 45 },
  { label: 'Recargando la cotización', percent: 85 },
];

// Texto del resultado del aviso de cancelación al cliente (respuesta del backend: clientNotice)
const cancellationNoticeText = (data) => {
  if (!data || !data.clientNotice) return null;
  if (data.clientNotice === 'SENT') return { severity: 'success', text: `Se avisó al cliente de la cancelación (${data.recipient}).` };
  if (data.clientNotice === 'SENDING') return { severity: 'info', text: `El aviso de cancelación va en camino a ${data.recipient}; llegará en unos minutos.` };
  if (data.clientNotice === 'NOT_SENT') return { severity: 'info', text: 'No se avisó al cliente: la cotización nunca se le envió.' };
  return { severity: 'warning', text: data.clientNoticeMessage || 'No se pudo avisar al cliente de la cancelación.' };
};

const QUOTATION_STATUS_CONFIG = {
  DRAFT: { label: 'Borrador (no enviada)', color: '#5c6bc0' },
  PENDING: { label: 'Pendiente', color: '#ed6c02' },
  ACCEPTED: { label: 'Aceptada', color: '#388e3c' },
  REJECTED: { label: 'Rechazada', color: '#d32f2f' },
  EXPIRED: { label: 'Expirada', color: '#757575' },
  CANCELLED: { label: 'Cancelada', color: '#9c27b0' },
  COMPLETED: { label: 'Completada', color: '#1976d2' },
  INVOICED: { label: 'Facturada', color: '#0288d1' },
  PAID: { label: 'Pagada', color: '#2e7d32' }
};

const NewQuotation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const editQuotationId = searchParams.get('edit');

  const [selectedServices, setSelectedServices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loadingQuotation, setLoadingQuotation] = useState(!!editQuotationId);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [quotationWorkOrder, setQuotationWorkOrder] = useState(null);
  const [quotationStatus, setQuotationStatus] = useState(null);
  const [isAcceptedLock, setIsAcceptedLock] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [sendPromptOpen, setSendPromptOpen] = useState(false);
  const [quotationSentAt, setQuotationSentAt] = useState(null);
  const [quotationIsLatest, setQuotationIsLatest] = useState(true);
  const [resendingCancelNotice, setResendingCancelNotice] = useState(false);
const [saveProgress, setSaveProgress] = useState(null);
  const pendingSaveFinish = useRef(null);
  const [, setSaveTick] = useState(0);
  // Mientras guarda, refresca el contador de segundos (los pasos largos no cambian de estado)
  useEffect(() => {
    if (saveProgress?.status !== 'RUNNING') return undefined;
    const timer = setInterval(() => setSaveTick(t => t + 1), 200);
    return () => clearInterval(timer);
  }, [saveProgress?.status]);
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [sendingQuotation, setSendingQuotation] = useState(false);
  const [sendJob, setSendJob] = useState(null);
  const sendPollActive = useRef(false);
  const sendCardRef = useRef(null);
  const sendCardShown = Boolean(sendJob);
  useEffect(() => {
    if (sendCardShown) sendCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [sendCardShown]);
  
  // Estado para precios editables de servicios principales
  const [editablePrices, setEditablePrices] = useState({});
  const [minimumServicePrices, setMinimumServicePrices] = useState({});

  // Función helper para concatenar el nombre completo del contacto corporativo
  const getCorporateContactFullName = (company) => {
    if (!company) return '';
    return [
      company.corporateContactTitle,
      company.corporateContactFirstName,
      company.corporateContactPaternalSurname,
      company.corporateContactMaternalSurname
    ].filter(Boolean).join(' ').trim();
  };

  const [form, setForm] = useState({
    email: '',
    name: '',
    phone_number: '',
    business_sector: '',
    service_type: 'PROTECCION_CIVIL',
    attention_group: 'VERACRUZ',
    employees_count: '',
    legal_representative: '',
    work_schedule_start: '08:00',
    work_schedule_end: '19:00',
    work_days: {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false
    },
    requires_invoice: false,
    risk_level: 'BAJO',
    client_type: 'NUEVO',
    priority: 1,
    business_name: '',
    company_legal_name: '',
    fiscal_address: '',
    rfc: '',
    comments: '',
  });
  
  // Estado para sustancias peligrosas
  const [hazardousSubstancesList, setHazardousSubstancesList] = useState([]);
  const [noHazardousSubstances, setNoHazardousSubstances] = useState(false);
  const [newHazardousSubstance, setNewHazardousSubstance] = useState({
    substanceName: '',
    quantity: '',
    unit: 'Litros'
  });
  
  // Estado para catálogo de sustancias peligrosas
  const [hazardousSubstancesCatalog, setHazardousSubstancesCatalog] = useState([]);
  // Estado para configuración de costos por sustancia
  const [substanceCostCatalog, setSubstanceCostCatalog] = useState([]);
  const [substanceModalOpen, setSubstanceModalOpen] = useState(false);

  // Estado para servicios adicionales
  const [additionalServices, setAdditionalServices] = useState([]);
  const [newService, setNewService] = useState({
    serviceDescription: '',
    unitPrice: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [folio, setFolio] = useState('');
  
  // Snackbar para errores de validación
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  // Alcance de la cotización y sucursales
  const [applicabilityScope, setApplicabilityScope] = useState('MATRIX_ONLY');
  const [companyBranches, setCompanyBranches] = useState([]);
  const [packageCoveragePoints, setPackageCoveragePoints] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);

  // Estado para entes seleccionados (matriz + sucursales) con configuración individual
  const [selectedEntities, setSelectedEntities] = useState([]);
  const [expandedEntity, setExpandedEntity] = useState(null);
  
  // Estado para formularios temporales por ente
  const [entityForms, setEntityForms] = useState({});

  // Generar folio automáticamente
  const generateFolio = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    const consecutive = Math.floor(Math.random() * 9999) + 1; // Número consecutivo aleatorio por ahora
    return `COT-${year}${month}${day}${consecutive.toString().padStart(4, '0')}`;
  };

  useEffect(() => {
    setFolio(generateFolio());
    fetchCompanies();
    fetchHazardousSubstancesCatalog();
    fetchSubstanceCosts();
  }, []);

  // Cargar cotización existente si hay parámetro edit
  useEffect(() => {
    if (editQuotationId) {
      loadQuotationForEdit(editQuotationId);
    }
  }, [editQuotationId]);

  // Recalcular montos de recargo cuando cambian los precios de servicios
  useEffect(() => {
    setSelectedEntities(prev => {
      let anyChanged = false;
      const updated = prev.map(entity => {
        const hasRecargas = (entity.additionalServices || []).some(s => s._isSubstanceRecargo && s._recargoPct !== undefined);
        if (!hasRecargas) return entity;
        const servicesBase = (entity.selectedServices || []).reduce((sum, s) => {
          const price = editablePrices[s.id] !== undefined ? Number(editablePrices[s.id]) : (s.price || 0);
          return sum + price;
        }, 0);
        let entityChanged = false;
        const newAdditionalServices = (entity.additionalServices || []).map(svc => {
          if (!svc._isSubstanceRecargo || svc._recargoPct === undefined) return svc;
          const newAmount = Math.round(((servicesBase * svc._recargoPct / 100) + (svc._recargoFixed || 0)) * 100) / 100;
          const curAmount = Math.round(parseFloat(svc.unitPrice || 0) * 100) / 100;
          if (newAmount === curAmount) return svc;
          entityChanged = true;
          return { ...svc, unitPrice: newAmount };
        });
        if (!entityChanged) return entity;
        anyChanged = true;
        return { ...entity, additionalServices: newAdditionalServices };
      });
      return anyChanged ? updated : prev;
    });
  }, [editablePrices]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-cargar sustancias peligrosas de cada ente cuando se crean
  useEffect(() => {
    if (selectedEntities.length > 0 && selectedCompany) {
      console.log('🔄 Verificando pre-carga de sustancias para entes...');
      
      // Verificar si algún ente necesita pre-carga (solo los que NO tienen el array inicializado)
      const needsPreload = selectedEntities.some(entity => 
        entity.hazardousSubstancesList === undefined
      );

      if (!needsPreload) {
        console.log('⏭️ Todos los entes ya tienen el array de sustancias inicializado');
        return;
      }
      
      const updatedEntities = selectedEntities.map(entity => {
        // Si el ente ya tiene el array inicializado (aunque esté vacío), no hacer nada
        if (entity.hazardousSubstancesList !== undefined) {
          return entity;
        }

        // Obtener sustancias del ente
        let entitySubstances = '';
        
        if (entity.id === 'MATRIZ') {
          // Para la matriz ya no hay precarga desde empresa
          entitySubstances = '';
        } else {
          // Para sucursales, buscar en companyBranches
          const branch = companyBranches.find(b => b.id === entity.id);
          entitySubstances = branch?.hazardousSubstances || '';
        }

        console.log(`🔍 Sustancias de ${entity.name}:`, entitySubstances);

        if (entitySubstances) {
          const substancesList = entitySubstances
            .split(',')
            .map(s => s.trim())
            .filter(s => s && s !== 'Ninguna')
            .map((substanceName, index) => ({
              id: `entity-${entity.id}-${Date.now()}-${index}`,
              substanceName: substanceName,
              quantity: '',
              unit: 'Litros',
              fromEntity: true
            }));

          console.log(`✅ Pre-cargadas ${substancesList.length} sustancias para ${entity.name}`);
          
          return {
            ...entity,
            hazardousSubstancesList: substancesList
          };
        }

        // Si no hay sustancias, inicializar con array vacío
        return {
          ...entity,
          hazardousSubstancesList: []
        };
      });

      setSelectedEntities(updatedEntities);
    }
  }, [selectedEntities, selectedCompany, companyBranches]);

  // Función para cargar cotización existente
  const loadQuotationForEdit = async (quotationId) => {
    setLoadingQuotation(true);
    setIsEditMode(true);
    try {
      console.log('🔍 Intentando cargar cotización ID:', quotationId);
      const response = await api.get(`/quotations/${quotationId}`);
      const quotation = response.data;
      
      console.log('📋 Cotización recibida del backend:', quotation);
      console.log('📋 Estructura de datos:', {
        id: quotation.id,
        invoiceFolio: quotation.invoiceFolio,
        email: quotation.email,
        name: quotation.name,
        companyId: quotation.companyId,
        serviceId: quotation.serviceId
      });
      
      // Cargar datos básicos con manejo seguro
      const parseWorkDays = (workDays) => {
        try {
          if (!workDays) return {
            monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
            saturday: false, sunday: false
          };
          if (typeof workDays === 'string') {
            return JSON.parse(workDays);
          }
          return workDays;
        } catch (e) {
          console.warn('Error parseando workDays:', e);
          return {
            monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
            saturday: false, sunday: false
          };
        }
      };

      const parseWorkSchedule = (schedule) => {
        try {
          if (!schedule) return { start: '08:00', end: '19:00' };
          const parts = schedule.split('-');
          return {
            start: parts[0]?.trim() || '08:00',
            end: parts[1]?.trim() || '19:00'
          };
        } catch (e) {
          console.warn('Error parseando workSchedule:', e);
          return { start: '08:00', end: '19:00' };
        }
      };

      const workSchedule = parseWorkSchedule(quotation.workSchedule);

      setForm({
        email: quotation.email || '',
        name: quotation.name || '',
        phone_number: quotation.phoneNumber || '',
        business_name: quotation.businessName || '',
        business_sector: quotation.businessSector || '',
        employees_count: quotation.employeesCount || '',
        company_legal_name: quotation.companyLegalName || '',
        fiscal_address: quotation.fiscalAddress || '',
        rfc: quotation.rfc || '',
        risk_level: quotation.riskLevel || '',
        client_type: quotation.clientType || '',
        priority: quotation.priorityId || 1,
        service_type: quotation.serviceType || '',
        attention_group: quotation.attentionGroup || '',
        legal_representative: quotation.legalRepresentative || '',
        work_schedule_start: workSchedule.start,
        work_schedule_end: workSchedule.end,
        work_days: parseWorkDays(quotation.workDays),
        civil_protection_approval: quotation.civilProtectionApproval || '',
        requires_invoice: quotation.requiresInvoice || false,
        comments: quotation.comments || ''
      });
      
      console.log('✅ Formulario cargado correctamente');
      
      // Bloquear edición si ya tiene orden de trabajo o está ACEPTADA, o si ya se le envió al
      // cliente (a partir de ahí solo se reenvía sin cambios o se reemplaza por una nueva versión)
      const acceptedLock = !!quotation.workOrderNumber || quotation.status === 'ACCEPTED';
      const editable = !quotation.status || quotation.status === 'DRAFT'
        || (quotation.status === 'PENDING' && !quotation.sentAt);
      setIsAcceptedLock(acceptedLock);
      setIsReadOnly(acceptedLock || !editable);
      setSendJob(null);
      setQuotationWorkOrder(quotation.workOrderNumber || null);
      setQuotationStatus(quotation.status || null);
      setQuotationSentAt(quotation.sentAt || null);
      setQuotationIsLatest(quotation.isLatestVersion !== false);
      api.get(`/quotations/${quotationId}/email-recipient`)
        .then(res => setEmailRecipient(res.data))
        .catch(() => setEmailRecipient(null));

      // Cargar folio
      setFolio(quotation.invoiceFolio || generateFolio());
      setPackageCoveragePoints((quotation.packageCoveragePoints || []).map((p, i) => ({ _key: String(p.id || ("loaded-" + i)), id: p.id, serviceId: p.serviceId, serviceName: p.serviceName || "", description: p.description || "", sortOrder: p.sortOrder !== undefined ? p.sortOrder : i, included: p.included !== false })));
      
      // Cargar empresa si existe
      if (quotation.companyId) {
        try {
          console.log('🏢 Cargando empresa ID:', quotation.companyId);
          const companiesResponse = await api.get('/companies');
          const company = companiesResponse.data.find(c => c.id === quotation.companyId);
          if (company) {
            setSelectedCompany(company);
            console.log('✅ Empresa cargada:', company.businessName);
            
            // Cargar sucursales usando la función centralizada
            if (company.companyType === 'MATRIZ') {
              await fetchCompanyBranches(company.id);
            }
          }
        } catch (companyErr) {
          console.warn('⚠️ Error al cargar empresa:', companyErr.message);
        }
      }
      
      // Cargar alcance y sucursales
      try {
        if (quotation.applicabilityScope) {
          setApplicabilityScope(quotation.applicabilityScope);
          if (quotation.branchIds && quotation.branchIds.length > 0) {
            setSelectedBranches(quotation.branchIds);
          }
          console.log('✅ Alcance cargado:', quotation.applicabilityScope);
        }
      } catch (scopeErr) {
        console.warn('⚠️ Error al cargar alcance:', scopeErr.message);
      }
      
      // Cargar servicio principal y filtrar servicios adicionales
      if (quotation.serviceId) {
        try {
          console.log('🔧 Cargando servicio principal ID:', quotation.serviceId);
          const serviceResponse = await api.get(`/services/${quotation.serviceId}`);
          const mainService = serviceResponse.data;
          setSelectedServices([mainService]);
          console.log('✅ Servicio principal cargado:', mainService.serviceName);
          
          // Buscar los servicios principales en additionalServices para obtener el precio base editado
          if (quotation.additionalServices && quotation.additionalServices.length > 0) {
            const mainServiceRows = quotation.additionalServices.filter(
              service => service.notes && service.notes.includes('Servicio principal')
            );

            if (mainServiceRows.length > 0) {
              let basePrice;
              if (mainServiceRows.length > 1) {
                // Formato NUEVO: 1 fila por ente, cada una con unitPrice = basePrice
                basePrice = mainServiceRows[0].unitPrice;
                console.log(`💰 Precio base (formato nuevo, ${mainServiceRows.length} filas): $${basePrice}`);
              } else {
                // Formato ANTIGUO: 1 sola fila con unitPrice = basePrice × entes
                const totals = calculatePreviewTotals();
                const entities = totals.entities || 1;
                basePrice = mainServiceRows[0].unitPrice / entities;
                console.log(`💰 Precio base (formato antiguo): $${mainServiceRows[0].unitPrice} / ${entities} entes = $${basePrice}`);
              }
              setEditablePrices({ [quotation.serviceId]: basePrice });
              setMinimumServicePrices({ [quotation.serviceId]: basePrice });
            } else if (quotation.serviceAmount) {
              setEditablePrices({ [quotation.serviceId]: quotation.serviceAmount });
              setMinimumServicePrices({ [quotation.serviceId]: quotation.serviceAmount });
            }
          }
        } catch (serviceErr) {
          console.warn('⚠️ Error al cargar servicio principal:', serviceErr.message);
        }
      }
      
      // Cargar SOLO servicios adicionales reales (excluir servicios principales)
      try {
        if (quotation.additionalServices && quotation.additionalServices.length > 0) {
          console.log('📦 Total de servicios en additionalServices:', quotation.additionalServices.length);
          console.log('📦 Datos completos de additionalServices:', JSON.stringify(quotation.additionalServices, null, 2));
          
          // Filtrar: EXCLUIR servicios que tienen "Servicio principal" en sus notas
          const additionalServicesOnly = quotation.additionalServices.filter(service => {
            console.log('🔍 Analizando servicio:', {
              description: service.serviceDescription,
              notes: service.notes,
              hasNotes: !!service.notes,
              includesPattern: service.notes ? service.notes.includes('Servicio principal') : false
            });
            
            const isMainService = service.notes && service.notes.includes('Servicio principal');
            if (isMainService) {
              console.log('🎯 Servicio principal detectado (EXCLUIDO de adicionales):', service.serviceDescription);
            } else {
              console.log('➕ Servicio adicional real (INCLUIDO):', service.serviceDescription);
            }
            return !isMainService;
          });
          
          console.log('➕ Servicios adicionales reales (después de filtrar):', additionalServicesOnly.length);
          console.log('➕ Lista filtrada:', additionalServicesOnly.map(s => s.serviceDescription));
          
          setAdditionalServices(additionalServicesOnly.map(service => ({
            id: service.id || Date.now() + Math.random(),
            serviceDescription: service.serviceDescription || '',
            unitPrice: service.unitPrice || 0,
            notes: service.notes || ''
          })));
          
          console.log('✅ Servicios adicionales cargados (sin duplicar principales)');
        }
      } catch (addServErr) {
        console.warn('⚠️ Error al cargar servicios adicionales:', addServErr.message);
      }
      
      // Cargar sustancias peligrosas si existen
      try {
        if (quotation.hazardousSubstancesList && quotation.hazardousSubstancesList.length > 0) {
          setHazardousSubstancesList(quotation.hazardousSubstancesList.map(substance => ({
            id: substance.id || Date.now() + Math.random(),
            substanceName: substance.substanceName || '',
            quantity: substance.quantity || '',
            unit: substance.unit || 'Litros'
          })));
          console.log('✅ Sustancias peligrosas cargadas:', quotation.hazardousSubstancesList.length);
        }
      } catch (hazardErr) {
        console.warn('⚠️ Error al cargar sustancias peligrosas:', hazardErr.message);
      }

      // 🏢 Cargar configuración de entes (matriz + sucursales) con servicios, sustancias y adicionales
      try {
        console.log('🏢 Cargando entes de la cotización...');
        const [entitiesResponse, allServicesResponse] = await Promise.all([
          api.get(`/quotations/${quotationId}/entities`),
          api.get('/services')
        ]);
        const entitiesData = entitiesResponse.data || [];
        const allServices = allServicesResponse.data || [];
        console.log(`📦 ${entitiesData.length} entes recibidos`);

        if (entitiesData.length > 0) {
          const reconstructedEntities = entitiesData.map(ent => {
            const isMatriz = ent.entityType === 'MATRIZ';
            const entityId = isMatriz ? 'MATRIZ' : ent.branchId;

            // Mapear serviceIds a objetos de servicio completos (para precio, nombre, etc.)
            const entitySelectedServices = (ent.serviceIds || [])
              .map(sid => allServices.find(s => s.id === sid))
              .filter(Boolean);

            const reconstructed = {
              id: entityId,
              name: ent.entityName || '',
              branchName: !isMatriz ? ent.entityName : undefined,
              type: ent.entityType,
              isMatrix: isMatriz,
              address: ent.address || '',
              // Campos del formulario (snake_case, strings)
              employees_count: ent.employeesCount != null ? String(ent.employeesCount) : '',
              construction_area: ent.constructionArea != null ? String(ent.constructionArea) : '',
              land_area: ent.landArea != null ? String(ent.landArea) : '',
              construction_levels: ent.constructionLevels != null ? String(ent.constructionLevels) : '',
              risk_level: ent.riskLevel || '',
              workSchedule: ent.workSchedule || '',
              workDays: ent.workDays || '',
              legalRepresentative: ent.legalRepresentative || '',
              inspectorContact: ent.inspectorContact || '',
              hazardousSubstances: ent.hazardousSubstances || '',
              // Servicios por ente (objetos completos)
              selectedServices: entitySelectedServices,
              // Sustancias peligrosas por ente
              hazardousSubstancesList: (ent.hazardousSubstancesList || []).map((s, i) => ({
                id: `loaded-${entityId}-${i}-${Date.now()}`,
                substanceName: s.substanceName || '',
                quantity: s.quantity != null ? String(s.quantity) : '',
                unit: s.unit || 'Litros'
              })),
              // Servicios adicionales por ente
              additionalServices: (ent.additionalServices || []).map((s, i) => ({
                id: Date.now() + i + Math.random(),
                serviceDescription: s.serviceDescription || '',
                unitPrice: s.unitPrice || 0,
                notes: s.notes || ''
              }))
            };

            console.log(`✅ Ente reconstruido: ${reconstructed.name}`, {
              servicios: entitySelectedServices.length,
              sustancias: reconstructed.hazardousSubstancesList.length,
              adicionales: reconstructed.additionalServices.length
            });

            return reconstructed;
          });

          // Redistribuir servicios adicionales globales a sus entes
          const globalAddServices = (quotation.additionalServices || []).filter(
            s => !(s.notes && s.notes.includes('Servicio principal'))
          );
          const matchedIds = new Set();
          const entitiesWithAdditional = reconstructedEntities.map(entity => {
            const entityLabel = entity.name || entity.branchName || '';
            if (!entityLabel) return entity;
            const suffix = ` (${entityLabel})`;
            const matched = globalAddServices.filter(s =>
              s.notes && (s.notes.endsWith(suffix) || s.notes === `(${entityLabel})`)
            ).map(s => {
              matchedIds.add(s.id);
              const cleanNotes = s.notes.endsWith(suffix)
                ? s.notes.slice(0, -suffix.length).trim()
                : '';
              return {
                id: s.id || Date.now() + Math.random(),
                serviceDescription: s.serviceDescription || '',
                unitPrice: s.unitPrice || 0,
                notes: cleanNotes
              };
            });
            return matched.length > 0
              ? { ...entity, additionalServices: [...(entity.additionalServices || []), ...matched] }
              : entity;
          });

          setSelectedEntities(entitiesWithAdditional);

          // Mantener en estado global solo los que no fueron asignados a un ente
          const remainingGlobal = globalAddServices.filter(s => !matchedIds.has(s.id));
          setAdditionalServices(remainingGlobal.map(s => ({
            id: s.id || Date.now() + Math.random(),
            serviceDescription: s.serviceDescription || '',
            unitPrice: s.unitPrice || 0,
            notes: s.notes || ''
          })));
          console.log(`✅ ${entitiesWithAdditional.length} entes cargados. Adicionales redistribuidos: ${matchedIds.size}, globales restantes: ${remainingGlobal.length}`);

          const restored = restoreQuotationLines(quotation.additionalServices, reconstructedEntities);
          if (restored) {
            setSelectedEntities(restored.entities);
            setAdditionalServices(restored.globalAdditional);
            setEditablePrices(prev => ({ ...prev, ...restored.editablePrices }));
          }
        }
      } catch (entitiesErr) {
        console.warn('⚠️ Error al cargar entes:', entitiesErr.message);
      }
      
      setSnackbar({
        open: true,
        message: (acceptedLock || !editable)
          ? `Cotización ${quotation.invoiceFolio} cargada en modo solo lectura`
          : `Cotización ${quotation.invoiceFolio} cargada para edición`,
        severity: 'info'
      });
      
    } catch (error) {
      console.error('❌ Error completo al cargar cotización:', error);
      console.error('❌ Detalles del error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        stack: error.stack
      });
      
      const errorMessage = error.response?.data?.message 
        || error.message 
        || 'Error al cargar la cotización para edición';
      
      setSnackbar({
        open: true,
        message: `Error: ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setLoadingQuotation(false);
      if (pendingSaveFinish.current) {
        const { startedAt, folio: savedFolio } = pendingSaveFinish.current;
        pendingSaveFinish.current = null;
        setSaveProgress({ status: 'DONE', label: `Cotización ${savedFolio} guardada`, percent: 100, elapsedMs: Date.now() - startedAt });
      }
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      const activeCompanies = response.data.filter(company => company.isActive);
      console.log('📋 Empresas cargadas:', activeCompanies.length);
      console.log('🔍 Primera empresa (ejemplo):', activeCompanies[0]);
      setCompanies(activeCompanies);
    } catch (err) {
      console.error('Error fetching companies:', err);
      const isNetworkError = !err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
      setError(isNetworkError ? 'network' : 'server');
    }
  };

  // Función para cargar configuración de costos por sustancia peligrosa
  const fetchSubstanceCosts = async () => {
    try {
      const response = await api.get('/hazardous-substance-costs');
      setSubstanceCostCatalog(response.data || []);
      console.log('✅ Costos de sustancias cargados:', (response.data || []).length);
    } catch (err) {
      console.warn('No se pudieron cargar costos de sustancias:', err);
    }
  };

  // Función para cargar catálogo de sustancias peligrosas
  const fetchHazardousSubstancesCatalog = async () => {
    try {
      const response = await api.get('/hazardous-substances-catalog');
      setHazardousSubstancesCatalog(response.data);
      console.log('✅ Catálogo de sustancias peligrosas cargado:', response.data.length);
    } catch (err) {
      console.error('Error al cargar catálogo de sustancias:', err);
      // No mostrar error al usuario, solo usar lista vacía
    }
  };

  // Handler cuando se agrega una nueva sustancia al catálogo
  const handleSubstanceAdded = async (newSubstance) => {
    await fetchHazardousSubstancesCatalog();
    setSnackbar({ 
      open: true, 
      message: `Sustancia "${newSubstance.name}" agregada al catálogo`, 
      severity: 'success' 
    });
  };

  // Función para construir domicilio fiscal desde campos separados
  const buildFiscalAddress = (company) => {
    if (!company) return '';
    
    const parts = [];
    
    // Calle
    if (company.fiscalStreet) parts.push(company.fiscalStreet);
    
    // Número exterior
    if (company.fiscalExteriorNumber) parts.push(`#${company.fiscalExteriorNumber}`);
    
    // Número interior
    if (company.fiscalInteriorNumber) parts.push(`Int. ${company.fiscalInteriorNumber}`);
    
    // Colonia
    if (company.fiscalNeighborhood) parts.push(company.fiscalNeighborhood);
    
    // Ciudad (Municipio)
    if (company.fiscalMunicipality) parts.push(company.fiscalMunicipality);
    
    // Estado
    if (company.fiscalState) parts.push(company.fiscalState);
    
    // Código Postal
    if (company.fiscalPostalCode) parts.push(`C.P. ${company.fiscalPostalCode}`);
    
    return parts.join(', ');
  };

  const fetchCompanyBranches = async (companyId) => {
    if (!companyId) {
      setCompanyBranches([]);
      setSelectedBranches([]);
      return;
    }
    try {
      const response = await api.get(`/company-branches/company/${companyId}`);
      
      // Filtrar solo sucursales activas
      const activeBranches = (response.data || []).filter(branch => branch.isActive === true || branch.isActive === 1);
      
      console.log(`🏢 Sucursales activas cargadas para cotización: ${activeBranches.length} de ${response.data?.length || 0} totales`);
      
      // Log detallado de la primera sucursal para verificar datos
      if (activeBranches.length > 0) {
        console.log('📋 Datos de ejemplo de la primera sucursal:', {
          id: activeBranches[0].id,
          nombre: activeBranches[0].branchName,
          empleados: activeBranches[0].employeesCount,
          m2_construccion: activeBranches[0].constructionArea,
          m2_terreno: activeBranches[0].landArea,
          niveles: activeBranches[0].constructionLevels,
          address: activeBranches[0].address,
          street: activeBranches[0].street,
          exteriorNumber: activeBranches[0].exteriorNumber,
          neighborhood: activeBranches[0].neighborhood,
          city: activeBranches[0].city,
          state: activeBranches[0].state,
          postalCode: activeBranches[0].postalCode
        });
      }
      
      setCompanyBranches(activeBranches);
      setSelectedBranches([]);
      
      // Si no hay sucursales, establecer automáticamente "Solo matriz" y deshabilitar el dropdown
      if (activeBranches.length === 0) {
        setApplicabilityScope('MATRIX_ONLY');
        console.log('ℹ️ Sin sucursales - Alcance establecido automáticamente en "Solo matriz"');
      }
    } catch (err) {
      console.error('Error fetching company branches:', err);
      setCompanyBranches([]);
      setSelectedBranches([]);
      setApplicabilityScope('MATRIX_ONLY');
    }
  };

  const handleCompanySelect = (event, newValue) => {
    console.log('🏢 Empresa seleccionada (objeto completo):', newValue);
    
    setSelectedCompany(newValue);
    if (newValue) {
      // Construir domicilio fiscal desde campos separados
      const fiscalAddress = buildFiscalAddress(newValue);
      
      // Auto-llenar campos con información de la empresa seleccionada
      setForm(prev => ({
        ...prev,
        business_name: newValue.businessName,
        business_sector: newValue.businessSectorName || newValue.businessSector,
        company_legal_name: newValue.legalName,
        fiscal_address: fiscalAddress,
        rfc: newValue.rfc,
        legal_representative: newValue.legalRepresentative,
        // Auto-llenar información de contacto corporativo
        name: getCorporateContactFullName(newValue),
        email: newValue.corporateContactEmail || '',
        phone_number: newValue.corporateContactPhone || ''
      }));

      setHazardousSubstancesList([]);

      // Cargar sucursales activas de la empresa seleccionada
      fetchCompanyBranches(newValue.id);
    } else {
      setCompanyBranches([]);
      setSelectedBranches([]);
      setApplicabilityScope('MATRIX_ONLY');
      setHazardousSubstancesList([]);
    }
  };

  const handleServiceSelect = (service) => {
    const isSelected = selectedServices.find(s => s.id === service.id);
    if (isSelected) {
      setSelectedServices(selectedServices.filter(s => s.id !== service.id));
      setPackageCoveragePoints(prev => prev.filter(p => p.serviceId !== service.id));
      const newEditablePrices = { ...editablePrices };
      const newMinimumServicePrices = { ...minimumServicePrices };
      delete newEditablePrices[service.id];
      delete newMinimumServicePrices[service.id];
      setEditablePrices(newEditablePrices);
      setMinimumServicePrices(newMinimumServicePrices);
    } else {
      setSelectedServices([...selectedServices, service]);
      setEditablePrices({ ...editablePrices, [service.id]: service.price });
      setMinimumServicePrices({ ...minimumServicePrices, [service.id]: service.price });
      api.get("/services/" + service.id + "/coverage-points")
        .then(res => {
          const points = (res.data || []).map((p, i) => ({
            _key: "catalog-" + service.id + "-" + i + "-" + Date.now(),
            serviceId: service.id,
            serviceName: service.name || service.serviceName || "",
            description: p.description || "",
            sortOrder: p.sortOrder !== undefined ? p.sortOrder : i,
            included: p.included !== false
          }));
          if (points.length > 0) {
            setPackageCoveragePoints(prev => [...prev.filter(cp => cp.serviceId !== service.id), ...points]);
          }
        })
        .catch(() => {});
    }
  };

  const validate = () => {
    let temp = {};
    let errorMessages = [];
    
    // Validaciones con mensajes descriptivos para el Snackbar
    if (!form.email) {
      temp.email = 'Requerido';
      errorMessages.push('Email');
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      temp.email = 'Email inválido';
      errorMessages.push('Email válido');
    }
    
    if (!form.name) {
      temp.name = 'Requerido';
      errorMessages.push('Nombre del contacto');
    }
    
    if (!form.phone_number) {
      temp.phone_number = 'Requerido';
      errorMessages.push('Teléfono');
    }
    
    if (!selectedCompany) {
      temp.selectedCompany = 'Debe seleccionar una empresa.';
      errorMessages.push('Empresa');
    }
    
    if (!form.service_type) {
      temp.service_type = 'El tipo de servicio es obligatorio.';
      errorMessages.push('Tipo de servicio');
    }
    
    if (!form.attention_group) {
      temp.attention_group = 'El grupo de atención es obligatorio.';
      errorMessages.push('Grupo de atención');
    }
    
    // Validar que al menos un ente esté seleccionado
    if (!selectedEntities || selectedEntities.length === 0) {
      temp.selectedEntities = 'Debe seleccionar al menos un ente (matriz o sucursal).';
      errorMessages.push('Al menos un ente (matriz o sucursal)');
    }
    
    // VALIDACIONES REMOVIDAS: Campos que ahora están por ente
    // - construction_area, land_area, employees_count, construction_levels
    // - legal_representative
    // Estos campos ahora se manejan individualmente por cada ente seleccionado
    // - gas_capacity: Eliminado, el gas se maneja como sustancia peligrosa
    
    setErrors(temp);
    
    // Mostrar Snackbar con campos faltantes si hay errores
    if (errorMessages.length > 0) {
      const message = errorMessages.length === 1 
        ? `Falta completar: ${errorMessages[0]}`
        : errorMessages.length <= 3
        ? `Faltan completar: ${errorMessages.join(', ')}`
        : `Faltan completar ${errorMessages.length} campos: ${errorMessages.slice(0, 2).join(', ')} y ${errorMessages.length - 2} más`;
      
      setSnackbar({
        open: true,
        message: `⚠️ ${message}`,
        severity: 'warning'
      });
    }
    
    return Object.values(temp).every(x => x === '');
  };

  const handleChange = e => {
    const { name, value } = e.target;
    
    // Limitar campos numéricos a 5 dígitos
    if (name === 'employees_count') {
      const numericValue = value.replace(/[^0-9]/g, '');
      if (numericValue.length <= 5) {
        setForm({ ...form, [name]: numericValue });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
    
    setErrors({ ...errors, [name]: '' });
  };

  const handleDayChange = (day) => {
    setForm({
      ...form,
      work_days: {
        ...form.work_days,
        [day]: !form.work_days[day]
      }
    });
    setErrors({ ...errors, work_days: '' });
  };

  // Funciones para manejar entes (matriz + sucursales)
  const handleEntityToggle = (entity) => {
    const exists = selectedEntities.find(e => e.id === entity.id);
    if (exists) {
      setSelectedEntities(selectedEntities.filter(e => e.id !== entity.id));
    } else {
      const newEntity = {
        ...entity,
        // Asegurar que tenga el campo 'name' para visualización
        name: entity.name || entity.branchName || entity.businessName,
        // Configuración individual por ente - Precargar datos si existen
        employees_count: entity.employeesCount?.toString() || '',
        construction_area: entity.constructionArea?.toString() || '',
        land_area: entity.landArea?.toString() || '',
        construction_levels: entity.constructionLevels?.toString() || '',
        risk_level: entity.riskLevel || '',
        // Campos de dirección - ya vienen en entity, solo asegurar que existan
        street: entity.street || '',
        exteriorNumber: entity.exteriorNumber || '',
        interiorNumber: entity.interiorNumber || '',
        neighborhood: entity.neighborhood || '',
        city: entity.city || '',
        state: entity.state || '',
        postalCode: entity.postalCode || '',
        hazardousSubstancesList: [],
        selectedServices: [],
        additionalServices: []
      };
      setSelectedEntities([...selectedEntities, newEntity]);
      
      // Log para debugging - Mostrar TODOS los datos del ente
      console.log(`✅ Ente seleccionado: ${entity.isMatrix ? 'Matriz' : entity.branchName || entity.name}`, {
        id: entity.id,
        nombre: entity.name || entity.branchName,
        tipo: entity.type,
        empleados: entity.employeesCount,
        m2_construccion: entity.constructionArea,
        m2_terreno: entity.landArea,
        niveles: entity.constructionLevels,
        direccion_completa: entity.address,
        calle: entity.street,
        numero_ext: entity.exteriorNumber,
        colonia: entity.neighborhood,
        ciudad: entity.city,
        estado: entity.state,
        cp: entity.postalCode
      });
    }
  };

  // Rangos realistas para los datos del inmueble por ente -- antes se aceptaba cualquier texto
  // libre (0 empleados, 9999999 m², 500 niveles), lo que colaba errores de captura directo a la
  // cotización y de ahí a la orden de trabajo y los documentos generados.
  const ENTITY_FIELD_LIMITS = {
    employees_count:     { ...FIELD_LIMITS.employeesCount, label: 'empleados' },
    construction_area:   { ...FIELD_LIMITS.constructionArea, label: 'm² de construcción' },
    land_area:           { ...FIELD_LIMITS.landArea, label: 'm² de terreno' },
    construction_levels: { ...FIELD_LIMITS.constructionLevels, label: 'niveles' },
  };

  const getEntityFieldError = (field, rawValue) => {
    const limits = ENTITY_FIELD_LIMITS[field];
    if (!limits || rawValue === '' || rawValue == null) return null;
    const value = Number(rawValue);
    if (Number.isNaN(value)) return 'Debe ser un número';
    if (value < limits.min) return `Mínimo ${limits.min} ${limits.label}`;
    if (value > limits.max) return `Máximo ${limits.max.toLocaleString('es-MX')} ${limits.label}`;
    return null;
  };

  const updateEntityField = (entityId, field, value) => {
    const limits = ENTITY_FIELD_LIMITS[field];
    let sanitized = value;
    if (limits) {
      // Enteros para empleados/niveles; decimales para m² de construcción/terreno
      const allowDecimal = field === 'construction_area' || field === 'land_area';
      sanitized = value.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');
      if (allowDecimal) {
        // Solo un punto decimal
        const firstDot = sanitized.indexOf('.');
        if (firstDot !== -1) {
          sanitized = sanitized.slice(0, firstDot + 1) + sanitized.slice(firstDot + 1).replace(/\./g, '');
        }
      }
      // No dejar teclear más allá del máximo permitido
      if (sanitized !== '' && sanitized !== '.' && Number(sanitized) > limits.max) {
        return;
      }
    }
    setSelectedEntities(selectedEntities.map(entity =>
      entity.id === entityId ? { ...entity, [field]: sanitized } : entity
    ));
  };

  const getEntityForm = (entityId) => {
    return entityForms[entityId] || {
      hazardousSubstance: { substanceName: '', quantity: '', unit: 'Litros' },
      additionalService: { serviceDescription: '', unitPrice: '', notes: '' }
    };
  };

  const updateEntityForm = (entityId, formType, data) => {
    setEntityForms(prev => ({
      ...prev,
      [entityId]: {
        ...getEntityForm(entityId),
        [formType]: data
      }
    }));
  };

  const RISK_LEVEL_ORDER = { BAJO: 1, MEDIO: 2, ALTO: 3 };
  const RISK_LEVEL_LABELS = { BAJO: 'Bajo', MEDIO: 'Medio', ALTO: 'Alto' };

  // El riesgo de un ente ya no se captura a mano: se deriva de los servicios del catálogo
  // que tiene contratados (cada servicio puede traer su propio nivel de riesgo, o ninguno --
  // ej. Dictamen Eléctrico). Si dos servicios del mismo ente traen riesgos distintos, se
  // resuelve al más alto mientras se marca como conflicto para que se note en pantalla.
  const getEntityRiskInfo = (entity) => {
    const levels = [...new Set((entity.selectedServices || [])
      .map(s => s.riskLevel)
      .filter(Boolean))];
    if (levels.length === 0) return { value: null, conflict: false, levels: [] };
    if (levels.length === 1) return { value: levels[0], conflict: false, levels };
    const highest = levels.reduce((max, lvl) =>
      (RISK_LEVEL_ORDER[lvl] || 0) > (RISK_LEVEL_ORDER[max] || 0) ? lvl : max, levels[0]);
    return { value: highest, conflict: true, levels };
  };

  const handleEntityServiceToggle = (entityId, service) => {
    setSelectedEntities(selectedEntities.map(entity => {
      if (entity.id !== entityId) return entity;
      const services = entity.selectedServices || [];
      const exists = services.find(s => s.id === service.id);
      const newServices = exists ? services.filter(s => s.id !== service.id) : [...services, service];
      const newBase = newServices.reduce((sum, s) => {
        const price = editablePrices[s.id] !== undefined ? Number(editablePrices[s.id]) : (s.price || 0);
        return sum + price;
      }, 0);
      const newAdditionalServices = (entity.additionalServices || []).map(svc => {
        if (!svc._isSubstanceRecargo || svc._recargoPct === undefined) return svc;
        return { ...svc, unitPrice: (newBase * svc._recargoPct / 100) + (svc._recargoFixed || 0) };
      });
      return { ...entity, selectedServices: newServices, additionalServices: newAdditionalServices };
    }));
  };

  const handleAddHazardousSubstanceToEntity = async (entityId) => {
    const entityForm = getEntityForm(entityId);
    const substance = entityForm.hazardousSubstance;

    if (!substance.substanceName.trim() || !substance.quantity.trim()) {
      setSnackbar({
        open: true,
        message: 'Complete todos los campos de la sustancia',
        severity: 'warning'
      });
      return;
    }

    if (!(Number(substance.quantity) > 0)) {
      setSnackbar({
        open: true,
        message: 'La cantidad debe ser mayor a 0',
        severity: 'warning'
      });
      return;
    }

    const substanceUniqueId = Date.now();
    const newSubstance = {
      id: substanceUniqueId,
      substanceName: substance.substanceName,
      quantity: String(Number(substance.quantity)),
      unit: substance.unit
    };

    // Siempre obtener costos frescos del servidor para no depender del caché
    let liveCostCatalog = substanceCostCatalog;
    try {
      const costsResponse = await api.get('/hazardous-substance-costs');
      liveCostCatalog = costsResponse.data || [];
      setSubstanceCostCatalog(liveCostCatalog);
    } catch (err) {
      console.warn('No se pudieron actualizar costos de sustancias:', err);
    }

    // Buscar configuración de costo activa para esta sustancia (comparación case-insensitive)
    const costConfig = liveCostCatalog.find(
      c => c.substanceName?.trim().toLowerCase() === substance.substanceName?.trim().toLowerCase() && c.isActive
    );

    const entity = selectedEntities.find(e => e.id === entityId);
    let updatedAdditionalServices = entity ? [...(entity.additionalServices || [])] : [];
    let recargoMsg = '';

    if (costConfig) {
      const pct   = parseFloat(costConfig.percentage)  || 0;
      const fixed = parseFloat(costConfig.fixedAmount)  || 0;

      if (pct > 0 || fixed > 0) {
        // Base = suma de servicios principales del ente
        const servicesBase = (entity.selectedServices || []).reduce((sum, s) => {
          const price = editablePrices[s.id] !== undefined ? Number(editablePrices[s.id]) : (s.price || 0);
          return sum + price;
        }, 0);

        const recargoAmount = (servicesBase * pct / 100) + fixed;

        const parts = [];
        if (pct > 0)   parts.push(`${pct}% sobre servicios ($${servicesBase.toFixed(2)})`);
        if (fixed > 0) parts.push(`$${fixed.toFixed(2)} fijo`);

        updatedAdditionalServices = [
          ...updatedAdditionalServices,
          {
            id: `recargo-${substanceUniqueId}`,
            serviceDescription: `Recargo sustancia peligrosa: ${substance.substanceName}`,
            unitPrice: recargoAmount,
            notes: parts.join(' + '),
            _isSubstanceRecargo: true,
            _substanceId: substanceUniqueId,
            _recargoPct: pct,
            _recargoFixed: fixed
          }
        ];
        recargoMsg = ` • Recargo: $${recargoAmount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} (${parts.join(' + ')})`;
      }
    }

    setSelectedEntities(selectedEntities.map(ent =>
      ent.id === entityId
        ? {
            ...ent,
            hazardousSubstancesList: [...(ent.hazardousSubstancesList || []), newSubstance],
            additionalServices: updatedAdditionalServices
          }
        : ent
    ));

    updateEntityForm(entityId, 'hazardousSubstance', { substanceName: '', quantity: '', unit: 'Litros' });
    setSnackbar({
      open: true,
      message: recargoMsg ? `Sustancia agregada${recargoMsg}` : 'Sustancia agregada',
      severity: recargoMsg ? 'warning' : 'success'
    });
  };

  const handleRemoveHazardousSubstanceFromEntity = (entityId, substanceId) => {
    setSelectedEntities(selectedEntities.map(entity =>
      entity.id === entityId
        ? {
            ...entity,
            hazardousSubstancesList: (entity.hazardousSubstancesList || []).filter(s => s.id !== substanceId),
            additionalServices: (entity.additionalServices || []).filter(s => s._substanceId !== substanceId)
          }
        : entity
    ));
  };

  const handleAddAdditionalServiceToEntity = (entityId) => {
    const entityForm = getEntityForm(entityId);
    const service = entityForm.additionalService;

    if (!service.serviceDescription.trim() || !service.unitPrice) {
      setSnackbar({
        open: true,
        message: 'Complete descripción y precio del servicio',
        severity: 'warning'
      });
      return;
    }

    const newService = {
      id: Date.now(),
      serviceDescription: service.serviceDescription,
      unitPrice: parseFloat(service.unitPrice),
      notes: service.notes || ''
    };

    setSelectedEntities(selectedEntities.map(entity =>
      entity.id === entityId
        ? { ...entity, additionalServices: [...(entity.additionalServices || []), newService] }
        : entity
    ));

    updateEntityForm(entityId, 'additionalService', { serviceDescription: '', unitPrice: '', notes: '' });
    setSnackbar({ open: true, message: 'Servicio adicional agregado', severity: 'success' });
  };

  const handleRemoveAdditionalServiceFromEntity = (entityId, serviceId) => {
    setSelectedEntities(selectedEntities.map(entity =>
      entity.id === entityId
        ? { ...entity, additionalServices: (entity.additionalServices || []).filter(s => s.id !== serviceId) }
        : entity
    ));
  };

  // Función para actualizar precio editable de servicio principal
  const handleEditableServicePriceChange = (serviceId, newPrice) => {
    const parsedPrice = parseFloat(newPrice);
    const minPrice = minimumServicePrices[serviceId] !== undefined
      ? Number(minimumServicePrices[serviceId])
      : 0;
    const numericPrice = Number.isNaN(parsedPrice) ? minPrice : Math.max(parsedPrice, minPrice);

    setEditablePrices({
      ...editablePrices,
      [serviceId]: numericPrice
    });
  };

  // Función para actualizar precio de servicio adicional
  const handleEditableAdditionalServicePriceChange = (serviceId, newPrice) => {
    const numericPrice = parseFloat(newPrice) || 0;
    setAdditionalServices(additionalServices.map(service => 
      service.id === serviceId 
        ? { ...service, unitPrice: numericPrice }
        : service
    ));
  };

  // Funciones para manejar servicios adicionales
  const handleServiceChange = (field, value) => {
    setNewService({ ...newService, [field]: value });
  };

  const handleAddService = () => {
    if (!newService.serviceDescription.trim()) {
      setError('La descripción del servicio es obligatoria');
      return;
    }
    if (!newService.unitPrice || parseFloat(newService.unitPrice) <= 0) {
      setError('El precio unitario debe ser mayor a 0');
      return;
    }

    const service = {
      id: Date.now(), // ID temporal para el frontend
      serviceDescription: newService.serviceDescription,
      unitPrice: parseFloat(newService.unitPrice),
      notes: newService.notes
    };

    setAdditionalServices([...additionalServices, service]);
    setNewService({ serviceDescription: '', unitPrice: '', notes: '' });
    setError(null);
  };

  const handleRemoveService = (id) => {
    setAdditionalServices(additionalServices.filter(service => service.id !== id));
  };

  // Funciones para manejar sustancias peligrosas
  const handleHazardousSubstanceChange = (field, value) => {
    setNewHazardousSubstance({ ...newHazardousSubstance, [field]: value });
  };

  const handleAddHazardousSubstance = () => {
    if (!newHazardousSubstance.substanceName.trim()) {
      setError('El nombre de la sustancia es obligatorio');
      return;
    }
    if (!newHazardousSubstance.quantity.trim()) {
      setError('La cantidad es obligatoria');
      return;
    }

    const substance = {
      id: Date.now(), // ID temporal para el frontend
      substanceName: newHazardousSubstance.substanceName,
      quantity: newHazardousSubstance.quantity,
      unit: newHazardousSubstance.unit,
      fromEntity: false // Marca que fue agregada manualmente
    };

    setHazardousSubstancesList([...hazardousSubstancesList, substance]);
    setNewHazardousSubstance({ substanceName: '', quantity: '', unit: 'Litros' });
    setError(null);
  };

  const handleRemoveHazardousSubstance = (id) => {
    setHazardousSubstancesList(hazardousSubstancesList.filter(substance => substance.id !== id));
  };

  // Cálculo previo de totales para mostrar al usuario antes de guardar
  const calculatePreviewTotals = () => {
    // Número de entes según alcance
    let entities = 1;
    if (applicabilityScope === 'ALL_BRANCHES') {
      entities = 1 + (companyBranches ? companyBranches.length : 0);
    } else if (applicabilityScope === 'SPECIFIC_BRANCHES') {
      // Contar matriz + sucursales específicas seleccionadas
      const hasMatrix = selectedBranches ? selectedBranches.some(branch => branch.isMatrix) : false;
      const branchCount = selectedBranches ? selectedBranches.filter(branch => !branch.isMatrix).length : 0;
      entities = (hasMatrix ? 1 : 0) + branchCount;
    }

    // Suma de precios de servicios seleccionados (usando precios editables)
    const baseServiceCost = (selectedServices || []).reduce((sum, service) => {
      const price = editablePrices[service.id] !== undefined ? editablePrices[service.id] : (typeof service.price === 'number' ? service.price : 0);
      return sum + price;
    }, 0);

    // Total por servicios principales considerando entes
    const mainServicesTotal = baseServiceCost * entities;

    // Total de servicios adicionales
    const additionalServicesTotal = (additionalServices || []).reduce((sum, service) => {
      const price = typeof service.unitPrice === 'number' ? service.unitPrice : 0;
      return sum + price;
    }, 0);

    // Subtotal estimado (solo referencia visual)
    const estimatedSubtotal = mainServicesTotal + additionalServicesTotal;

    return {
      entities,
      baseServiceCost,
      mainServicesTotal,
      additionalServicesTotal,
      estimatedSubtotal
    };
  };


  const handleAddCoveragePoint = (serviceId, serviceName) => {
    setPackageCoveragePoints(prev => [...prev, { _key: "new-" + Date.now() + "-" + Math.random(), serviceId, serviceName, description: "", sortOrder: prev.filter(p => p.serviceId === serviceId).length, included: true }]);
  };

  const handleUpdateCoveragePoint = (_key, field, value) => {
    setPackageCoveragePoints(prev => prev.map(p => p._key === _key ? { ...p, [field]: value } : p));
  };

  const handleRemoveCoveragePoint = (_key) => {
    setPackageCoveragePoints(prev => prev.filter(p => p._key !== _key));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    if (isReadOnly) {
      setSnackbar({ open: true, message: `⛔ Esta cotización no puede editarse porque ya generó la orden de trabajo ${quotationWorkOrder}`, severity: 'warning' });
      return;
    }
    if (!validate()) return;
    
    // Determinar si es creación o actualización
    const isUpdate = isEditMode && editQuotationId;
    
    // Mostrar mensaje de espera apropiado
    setSnackbar({
      open: true,
      message: isUpdate ? '⏳ Actualizando cotización, por favor espere...' : '⏳ Creando cotización, por favor espere...',
      severity: 'info'
    });
    
    // Validar que al menos un ente esté seleccionado
    if (!selectedEntities || selectedEntities.length === 0) {
      setSnackbar({
        open: true,
        message: '⚠️ Debe seleccionar al menos un ente (matriz o sucursal) para crear la cotización',
        severity: 'warning'
      });
      return;
    }
    
    // Validar que cada ente tenga al menos un servicio seleccionado
    const entitiesWithoutServices = selectedEntities.filter(entity => 
      !entity.selectedServices || entity.selectedServices.length === 0
    );
    
    if (entitiesWithoutServices.length > 0) {
      const entityNames = entitiesWithoutServices.map(e => e.name).join(', ');
      setSnackbar({
        open: true,
        message: `⚠️ Los siguientes entes no tienen servicios seleccionados: ${entityNames}`,
        severity: 'warning'
      });
      return;
    }
    
    // Validar que los datos del inmueble por ente estén en rangos realistas
    const entityFieldErrors = [];
    selectedEntities.forEach(entity => {
      ['employees_count', 'construction_area', 'land_area', 'construction_levels'].forEach(field => {
        const fieldError = getEntityFieldError(field, entity[field]);
        if (fieldError) entityFieldErrors.push(`${entity.name}: ${fieldError}`);
      });
    });
    if (entityFieldErrors.length > 0) {
      setSnackbar({
        open: true,
        message: `⚠️ Datos del inmueble fuera de rango -- ${entityFieldErrors.join('; ')}`,
        severity: 'warning'
      });
      return;
    }

    // Validar empresa seleccionada
    if (!selectedCompany) {
      setSnackbar({
        open: true,
        message: '⚠️ Debe seleccionar una empresa para crear la cotización',
        severity: 'warning'
      });
      return;
    }

    const linesWithoutDescription = [
      ...selectedEntities.flatMap(entity => (entity.additionalServices || [])
        .filter(s => !(s.serviceDescription || '').trim())
        .map(() => entity.name)),
      ...(additionalServices || [])
        .filter(s => !(s.serviceDescription || '').trim())
        .map(() => 'Servicios generales')
    ];
    if (linesWithoutDescription.length > 0) {
      setSnackbar({
        open: true,
        message: `⚠️ Hay ${linesWithoutDescription.length} servicio(s) adicional(es) sin descripción en: ${[...new Set(linesWithoutDescription)].join(', ')}. Elimínalos y agrégalos de nuevo con descripción.`,
        severity: 'warning'
      });
      return;
    }
    setLoading(true);
    const saveStartedAt = Date.now();
    const enterSaveStep = (index) => setSaveProgress({
      status: 'RUNNING', stepNumber: index + 1, totalSteps: SAVE_STEPS.length,
      label: SAVE_STEPS[index].label, percent: SAVE_STEPS[index].percent, startedAt: saveStartedAt,
    });
    enterSaveStep(0);
    try {
      enterSaveStep(1);
      // Calcular totales antes de enviar
      const totals = calculatePreviewTotals();
      
      console.log('💰 FRONTEND - Totales calculados:', totals);
      console.log('💰 FRONTEND - totalAmount que se enviará:', totals.estimatedSubtotal);
      
      // Mapear campos del frontend (snake_case) al backend (camelCase)
      let resolvedCoveragePoints = packageCoveragePoints;
      if (resolvedCoveragePoints.length === 0) {
        const serviceIdSet = new Set();
        (selectedServices || []).forEach(s => s.id && serviceIdSet.add(s.id));
        (selectedEntities || []).forEach(e => (e.selectedServices || []).forEach(s => s.id && serviceIdSet.add(s.id)));
        if (serviceIdSet.size > 0) {
          const allPoints = await Promise.all(
            [...serviceIdSet].map(id =>
              api.get("/services/" + id + "/coverage-points")
                .then(r => (r.data || []).map((p, i) => ({ serviceId: id, description: p.description || "", sortOrder: p.sortOrder !== undefined ? p.sortOrder : i, included: p.included !== false })))
                .catch(() => [])
            )
          );
          resolvedCoveragePoints = allPoints.flat();
        }
      }

      const backendData = {
        email: form.email,
        name: form.name,
        phoneNumber: form.phone_number,
        businessName: selectedCompany?.businessName || form.business_name,
        businessSector: selectedCompany?.businessSector || form.business_sector,
        serviceType: form.service_type,
        attentionGroup: form.attention_group,
        employeesCount: parseInt(form.employees_count) || 0,
        companyLegalName: selectedCompany?.legalName || form.company_legal_name,
        fiscalAddress: selectedCompany?.fiscalAddress || form.fiscal_address,
        rfc: selectedCompany?.rfc || form.rfc,
        riskLevel: form.risk_level,
        clientType: form.client_type,
        priorityId: form.priority,
        legalRepresentative: selectedCompany?.legalRepresentative || form.legal_representative,
        workSchedule: form.work_schedule_start && form.work_schedule_end ? `${form.work_schedule_start} - ${form.work_schedule_end}` : '',
        workDays: Object.entries(form.work_days)
          .filter(([day, selected]) => selected)
          .map(([day, selected]) => {
            const dayNames = {
              monday: 'Lunes',
              tuesday: 'Martes', 
              wednesday: 'Miércoles',
              thursday: 'Jueves',
              friday: 'Viernes',
              saturday: 'Sábado',
              sunday: 'Domingo'
            };
            return dayNames[day];
          })
          .join(', '),
        comments: form.comments || '',
        requiresInvoice: form.requires_invoice,
        // Solo enviar invoiceFolio en actualizaciones, en creaciones dejar que backend lo genere con -V1
        ...(isUpdate && { invoiceFolio: folio }),
        // Nuevo: enviar el ID del servicio seleccionado (del global o del primer ente)
        serviceId: (() => {
          // Prioridad 1: servicio seleccionado globalmente (modelo antiguo)
          if (selectedServices[0]?.id) {
            console.log('DEBUG serviceId (global):', selectedServices[0].id);
            return selectedServices[0].id;
          }
          // Prioridad 2: primer servicio del primer ente (modelo per-entity)
          const firstEntityWithServices = selectedEntities.find(
            e => e.selectedServices && e.selectedServices.length > 0
          );
          const fromEntity = firstEntityWithServices?.selectedServices?.[0]?.id || null;
          console.log('DEBUG serviceId (desde ente):', fromEntity);
          return fromEntity;
        })(),
        // Agregar ID de la empresa seleccionada
        companyId: selectedCompany?.id || null,
        // 💰 y 💼 Construir servicios (principales + adicionales) consolidados desde TODOS los entes
        ...(() => {
          const { totalAmount: consolidatedTotal, additionalServices: allServices } =
            buildQuotationLines(selectedEntities, additionalServices, editablePrices);

          console.log(`🔍 FRONTEND - Servicios consolidados a enviar: ${allServices.length}`);
          console.log(`💰 FRONTEND - Total consolidado: $${consolidatedTotal}`);
          console.log(`📋 FRONTEND - Array completo:`, allServices);

          return {
            totalAmount: consolidatedTotal,
            additionalServices: allServices
          };
        })(),
        pricingInput: buildPricingInput(selectedEntities, additionalServices, editablePrices),
        // Agregar sustancias peligrosas
        hazardousSubstancesList: hazardousSubstancesList.map(substance => ({
          substanceName: substance.substanceName,
          quantity: substance.quantity,
          unit: substance.unit
        })),
        // Alcance de la cotización y sucursales específicas (si aplica)
        applicabilityScope,
        branchIds: selectedBranches.filter(branch => !branch.isMatrix).map(branch => branch.id),
        // Configuración por ente: guarda servicios, sustancias y servicios adicionales por cada ente
        entities: selectedEntities.map(entity => ({
          entityType: entity.id === 'MATRIZ' ? 'MATRIZ' : 'SUCURSAL',
          branchId: entity.id === 'MATRIZ' ? null : entity.id,
          entityName: entity.name || entity.branchName || entity.businessName || '',
          address: entity.address || '',
          employeesCount: parseInt(entity.employees_count) || null,
          constructionArea: parseFloat(entity.construction_area) || null,
          landArea: parseFloat(entity.land_area) || null,
          constructionLevels: parseInt(entity.construction_levels) || null,
          riskLevel: getEntityRiskInfo(entity).value,
          hazardousSubstances: entity.hazardousSubstances || '',
          workSchedule: entity.workSchedule || '',
          workDays: entity.workDays || '',
          legalRepresentative: entity.legalRepresentative || '',
          inspectorContact: entity.inspectorContact || '',
          serviceIds: (entity.selectedServices || []).map(s => s.id),
          hazardousSubstancesList: (entity.hazardousSubstancesList || []).map(s => ({
            substanceName: s.substanceName,
            quantity: s.quantity ? parseFloat(s.quantity) : null,
            unit: s.unit || 'Litros'
          })),
          additionalServices: (entity.additionalServices || []).map(s => ({
            serviceDescription: s.serviceDescription,
            unitPrice: parseFloat(s.unitPrice) || 0,
            notes: s.notes || ''
          }))
        })),
        packageCoveragePoints: resolvedCoveragePoints.map((p, i) => ({ serviceId: p.serviceId, description: p.description, sortOrder: p.sortOrder !== undefined ? p.sortOrder : i, included: p.included !== false }))

      };
      
      // Realizar POST (crear) o PUT (actualizar) según el modo
      enterSaveStep(2);
      let response;
      if (isUpdate) {
        console.log('🔄 Actualizando cotización ID:', editQuotationId);
        response = await api.put(`/quotations/${editQuotationId}`, backendData);
      } else {
        console.log('➕ Creando nueva cotización');
        response = await api.post('/quotations', backendData);
        
        // Actualizar folio con el generado por el backend (incluye -V1)
        if (response.data && response.data.invoiceFolio) {
          setFolio(response.data.invoiceFolio);
          console.log('📋 Folio generado por backend:', response.data.invoiceFolio);
        }
      }
      
      // Obtener folio final para mostrar en mensaje
      const finalFolio = response.data?.invoiceFolio || folio;
      
      // Cerrar mensaje de espera y mostrar éxito
      setSnackbar({
        open: true,
        message: isUpdate 
          ? `✅ Cotización ${finalFolio} actualizada exitosamente` 
          : `✅ Cotización ${finalFolio} creada exitosamente`,
        severity: 'success'
      });
      setSuccess(isUpdate 
        ? `Cotización ${finalFolio} actualizada exitosamente` 
        : `Cotización ${finalFolio} creada exitosamente`);
      
      // Si es actualización, recargar la cotización para mantener el contexto
      enterSaveStep(3);
      pendingSaveFinish.current = { startedAt: saveStartedAt, folio: finalFolio };
      if (isUpdate) {
        console.log('🔄 Recargando cotización actualizada ID:', editQuotationId);
        await loadQuotationForEdit(editQuotationId);
      } else {
        // Guardar ya NO envía el correo -- eso ahora es una acción explícita ("Enviar
        // Cotización"). Nos quedamos en la misma pantalla, en modo edición sobre la
        // cotización recién creada, para que esa acción quede disponible de inmediato
        // sin perder el contexto ni redirigir a la lista.
        setSearchParams({ edit: String(response.data.id) });
        setSendPromptOpen(true);
      }
    } catch (error) {
      console.error('Error creating quotation:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error al crear la cotización. Intente de nuevo.';
      pendingSaveFinish.current = null;
      setSaveProgress(prev => ({ ...(prev || {}), status: 'ERROR', error: errorMessage, elapsedMs: Date.now() - saveStartedAt }));
      
      // Cerrar mensaje de espera y mostrar error
      setSnackbar({
        open: true,
        message: `❌ ${errorMessage}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Acción explícita de envío, separada de "Guardar" -- reenvía/envía el mismo correo con
  // PDF que ya usa el resto del sistema (POST /quotations/{id}/send-email), disponible una
  // vez que la cotización ya tiene ID (fue guardada al menos una vez).
  // El backend envía en segundo plano y reporta el paso actual; aquí se consulta el avance
  // hasta que termine (DONE/ERROR) para mostrarlo en la barra de progreso.
  useEffect(() => () => { sendPollActive.current = false; }, []);

  const handleSendQuotation = async () => {
    if (!editQuotationId || sendingQuotation) return;
    setSendingQuotation(true);
    setSendJob(null);
    sendPollActive.current = true;
    try {
      let job = (await api.post(`/quotations/${editQuotationId}/send-email`)).data;
      setSendJob(job);
      let pollFailures = 0;
      while (job.status === 'RUNNING' && sendPollActive.current) {
        await new Promise(resolve => setTimeout(resolve, 700));
        try {
          job = (await api.get(`/quotations/send-jobs/${job.jobId}`)).data;
          pollFailures = 0;
          setSendJob(job);
        } catch (pollErr) {
          if (++pollFailures >= 5) throw pollErr;
        }
      }
      if (job.status === 'DONE') {
        setQuotationStatus(prev => (prev === 'DRAFT' ? 'PENDING' : prev));
        setIsReadOnly(true);
        setSnackbar({ open: true, message: `✅ Cotización enviada a ${job.recipient}`, severity: 'success' });
      } else if (job.status === 'ERROR') {
        setSnackbar({ open: true, message: `❌ ${job.error}`, severity: 'error' });
      }
    } catch (err) {
      const msg = err.response?.data?.error
        || (err.response ? `Error ${err.response.status} al enviar la cotización`
          : 'Se perdió la conexión con el servidor; verifica en unos minutos si el correo salió antes de reenviar');
      setSendJob(prev => (prev ? { ...prev, status: 'ERROR', error: msg } : { status: 'ERROR', error: msg, percent: 0 }));
      setSnackbar({ open: true, message: `❌ ${msg}`, severity: 'error' });
    } finally {
      sendPollActive.current = false;
      setSendingQuotation(false);
    }
  };

  // Cancela la versión enviada y abre la nueva (borrador, con todo copiado) para editarla y enviarla
  const handleResendCancellationNotice = async () => {
    setResendingCancelNotice(true);
    try {
      const { data } = await api.post(`/quotations/${editQuotationId}/cancellation-notice`);
      const notice = cancellationNoticeText(data);
      setSnackbar({ open: true, message: notice ? notice.text : 'Aviso procesado', severity: notice ? notice.severity : 'info' });
    } catch (err) {
      setSnackbar({ open: true, message: `❌ ${err.response?.data?.error || 'No se pudo reenviar el aviso de cancelación'}`, severity: 'error' });
    } finally {
      setResendingCancelNotice(false);
    }
  };

  const handleReplaceQuotation = async () => {
    setReplacing(true);
    try {
      const { data } = await api.post(`/quotations/${editQuotationId}/replace`);
      setReplaceDialogOpen(false);
      const notice = cancellationNoticeText(data);
      setSnackbar({
        open: true,
        message: `✅ Se creó la ${data.invoiceFolio}. Edítala y envíala al cliente.${notice ? ' ' + notice.text : ''}`,
        severity: notice && notice.severity === 'warning' ? 'warning' : 'success',
      });
      navigate(`/cotizaciones/new-quotation?edit=${data.id}`);
    } catch (err) {
      setSnackbar({ open: true, message: `❌ ${err.response?.data?.error || 'No se pudo crear la nueva versión'}`, severity: 'error' });
    } finally {
      setReplacing(false);
    }
  };

  const saveProgressCard = saveProgress && (
    <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, width: '100%', maxWidth: 720, mx: 'auto', borderColor: saveProgress.status === 'ERROR' ? '#e57373' : saveProgress.status === 'DONE' ? '#81c784' : '#90caf9' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: saveProgress.status === 'ERROR' ? '#c62828' : saveProgress.status === 'DONE' ? '#2e7d32' : '#1565c0' }}>
          {saveProgress.status === 'ERROR'
            ? '❌ No se pudo guardar la cotización'
            : saveProgress.status === 'DONE'
              ? `✅ ${saveProgress.label}`
              : `Paso ${saveProgress.stepNumber} de ${saveProgress.totalSteps}: ${saveProgress.label}…`}
        </Typography>
        {(saveProgress.elapsedMs != null || saveProgress.startedAt) && (
          <Typography variant="caption" sx={{ color: '#777', whiteSpace: 'nowrap' }}>
            {((saveProgress.status === 'RUNNING' ? Date.now() - saveProgress.startedAt : saveProgress.elapsedMs) / 1000).toFixed(1)} s
          </Typography>
        )}
      </Box>
      <LinearProgress
        variant={saveProgress.status === 'RUNNING' ? 'indeterminate' : 'determinate'}
        value={saveProgress.status === 'DONE' ? 100 : saveProgress.percent || 0}
        color={saveProgress.status === 'ERROR' ? 'error' : saveProgress.status === 'DONE' ? 'success' : 'primary'}
        sx={{ height: 8, borderRadius: 4 }}
      />
      {saveProgress.status === 'ERROR' && saveProgress.error && (
        <Typography variant="caption" sx={{ color: '#c62828', display: 'block', mt: 1 }}>
          {saveProgress.error}
        </Typography>
      )}
    </Paper>
  );

  if (loadingQuotation) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
          px: 2,
        }}
      >
        {saveProgress?.status === 'RUNNING' ? saveProgressCard : <CircularProgress size={60} />}
      </Box>
    );
  }

  if (error && typeof error === 'string') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          gap: 3,
          p: 4,
        }}
      >
        <svg width="220" height="180" viewBox="0 0 220 180" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="40" width="78" height="100" rx="10" fill="#e3f2fd" stroke="#90caf9" strokeWidth="2"/>
          <rect x="30" y="62" width="14" height="22" rx="4" fill="#1976d2" opacity="0.35"/>
          <rect x="54" y="62" width="14" height="22" rx="4" fill="#1976d2" opacity="0.35"/>
          <rect x="39" y="94" width="20" height="16" rx="10" fill="#1976d2" opacity="0.35"/>
          <path d="M 155 90 C 155 120, 140 140, 115 158" stroke="#ff7043" strokeWidth="7" fill="none" strokeLinecap="round"/>
          <rect x="130" y="68" width="42" height="36" rx="6" fill="#ff7043"/>
          <rect x="140" y="52" width="7" height="20" rx="3" fill="#bf360c"/>
          <rect x="153" y="52" width="7" height="20" rx="3" fill="#bf360c"/>
          <text x="97" y="72" fontSize="22" fill="#ffa726">⚡</text>
          <circle cx="108" cy="90" r="4" fill="#1976d2" opacity="0.5"/>
          <circle cx="120" cy="88" r="3" fill="#1976d2" opacity="0.35"/>
          <circle cx="130" cy="85" r="2" fill="#1976d2" opacity="0.2"/>
        </svg>
        <Typography variant="h5" fontWeight="bold" color="text.primary">
          Sin conexión al servidor
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" maxWidth={380}>
          {error === 'network'
            ? 'No se pudo conectar con el servidor. Verifica que el backend esté activo e intenta de nuevo.'
            : 'El servidor respondió con un error inesperado. Intenta recargar la página.'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={() => { setError(null); fetchCompanies(); }}
          sx={{ mt: 1, borderRadius: 2, px: 4 }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '95%',
      maxWidth: '1400px',
      mx: 'auto',
      mt: 2,
      mb:2,
      background: isAcceptedLock ? 'linear-gradient(180deg, #ffdbdb 0%, #fff0f0 100%)' : '#fff',
      border: isAcceptedLock ? '2px solid #e57373' : 'none',
      borderRadius: 4,
      boxShadow: isAcceptedLock ? '0 4px 32px rgba(183,28,28,0.28)' : '0 4px 32px rgba(0,0,0,0.10)',
      p: 4,
    }}>
      <Box sx={{ position: 'relative', mb: 3 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: '#222',
            fontFamily: "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
            textAlign: 'center',
          }}
        >
          {isReadOnly ? 'Visualizar Cotización' : isEditMode ? 'Editar Cotización' : 'Nueva Cotización'}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {isReadOnly ? (
            <Chip
              icon={<LockIcon sx={{ fontSize: 15, color: '#fff !important' }} />}
              label="SOLO LECTURA"
              sx={{ backgroundColor: '#d32f2f', color: '#fff', fontWeight: 600, fontSize: '0.8rem' }}
            />
          ) : isEditMode && (
            <Chip
              label="MODO EDICIÓN"
              sx={{
                backgroundColor: '#ff9800',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.8rem',
                '& .MuiChip-label': { px: 2, py: 0.5 }
              }}
            />
          )}
          {isEditMode && quotationStatus && (
            <Chip
              label={`Estatus: ${(QUOTATION_STATUS_CONFIG[quotationStatus] || { label: quotationStatus }).label}`}
              sx={{
                backgroundColor: (QUOTATION_STATUS_CONFIG[quotationStatus] || { color: '#757575' }).color,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.8rem',
                '& .MuiChip-label': { px: 2, py: 0.5 }
              }}
            />
          )}
          <Chip
            label={`Folio: ${folio}`}
            sx={{
              backgroundColor: '#1976d2',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.9rem',
              '& .MuiChip-label': {
                px: 2,
                py: 1
              }
            }}
          />
        </Box>
        {(() => {
          const closed = { REJECTED: 'Rechazada', CANCELLED: 'Cancelada', EXPIRED: 'Expirada' }[quotationStatus];
          const activeStep = quotationWorkOrder ? 3
            : quotationStatus === 'ACCEPTED' ? 2
            : quotationStatus === 'PENDING' || closed ? (isReadOnly ? 1 : 0)
            : 0;
          const labels = ['Borrador', closed || 'Enviada', 'Aceptada', 'Orden de trabajo'];
          const nextHint = !editQuotationId ? 'Siguiente: guardar la cotización'
            : activeStep === 0 ? 'Siguiente: enviarla al cliente'
            : activeStep === 1 && !closed ? 'Siguiente: esperar la respuesta del cliente'
            : null;
          return (
            <Box sx={{ mt: 2.5, maxWidth: 720, mx: 'auto' }}>
              <Stepper activeStep={closed ? 1 : activeStep} alternativeLabel>
                {labels.map((label, i) => (
                  <Step key={label} completed={i < activeStep || (i === activeStep && activeStep === 3)}>
                    <StepLabel error={!!closed && i === 1}>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
              {nextHint && (
                <Typography variant="body2" sx={{ textAlign: 'center', mt: 1, color: '#1565c0', fontWeight: 600 }}>
                  {nextHint}
                </Typography>
              )}
            </Box>
          );
        })()}
      </Box>


      {/* Sección 1: Selección de Empresa */}
      <Box sx={{ 
        mb: 4, 
        p: 3, 
        backgroundColor: '#f8f9fa', 
        borderRadius: 3, 
        border: '1px solid #e9ecef' 
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3, 
            color: '#2c3e50', 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '"🏢"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Selección de Empresa
        </Typography>
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => `${option.businessName} (${option.rfc || 'SIN RFC'})`}
              value={selectedCompany}
              onChange={handleCompanySelect}
              disabled={isReadOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar empresa *"
                  error={!!errors.selectedCompany}
                  helperText={errors.selectedCompany || "Seleccione una empresa del catálogo"}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    backgroundColor: '#fff',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '0.95rem',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                        borderWidth: 1
                      },
                      '&:hover fieldset': {
                        borderColor: '#1976d2',
                        borderWidth: 1
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1976d2',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      color: '#666'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {option.businessName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      RFC: {option.rfc} | {option.businessSectorName || option.businessSector || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText="No se encontraron empresas"
              loading={loading}
            />
          </Grid>

          
          {/* Información del contacto corporativo */}
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField 
              label="Contacto corporativo" 
              name="name" 
              value={selectedCompany ? getCorporateContactFullName(selectedCompany) : form.name} 
              onChange={handleChange} 
              disabled={!!selectedCompany}
              error={!!errors.name} 
              helperText={errors.name} 
              required 
              fullWidth 
              variant="outlined"
              size="small"
              sx={{ 
                backgroundColor: selectedCompany ? '#f5f5f5' : '#fff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 1
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  color: '#666'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField 
              label="Email corporativo" 
              name="email" 
              type="email"
              value={selectedCompany?.corporateContactEmail || form.email}
              placeholder={selectedCompany ? "Email corporativo de la empresa" : "Ingrese email del cliente"} 
              onChange={handleChange} 
              disabled={!!selectedCompany}
              error={!!errors.email} 
              helperText={errors.email} 
              required 
              fullWidth 
              variant="outlined"
              size="small"
              sx={{ 
                backgroundColor: selectedCompany ? '#f5f5f5' : '#fff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 1
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  color: '#666'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField 
              label="Teléfono corporativo" 
              name="phone_number" 
              value={selectedCompany?.corporateContactPhone || form.phone_number} 
              onChange={handleChange} 
              disabled={!!selectedCompany}
              error={!!errors.phone_number} 
              helperText={errors.phone_number} 
              required 
              fullWidth 
              variant="outlined"
              size="small"
              sx={{ 
                backgroundColor: selectedCompany ? '#f5f5f5' : '#fff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 1
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  color: '#666'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField 
              label="Representante legal" 
              name="legal_representative" 
              value={selectedCompany?.legalRepresentative || form.legal_representative} 
              onChange={handleChange} 
              disabled={!!selectedCompany}
              error={!!errors.legal_representative} 
              helperText={errors.legal_representative} 
              required 
              fullWidth 
              variant="outlined"
              size="small"
              sx={{ 
                backgroundColor: selectedCompany ? '#f5f5f5' : '#fff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 1
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  color: '#666'
                }
              }}
            />
          </Grid>

          {/* Datos Fiscales (Auto-llenados desde empresa) */}
          {selectedCompany && (
            <>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  label="Razón social" 
                  value={selectedCompany.legalName}
                  disabled
                  fullWidth 
                  variant="outlined"
                  size="small"
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '0.95rem'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      color: '#666'
                    }
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  label="RFC" 
                  value={selectedCompany.rfc}
                  disabled
                  fullWidth 
                  variant="outlined"
                  size="small"
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '0.95rem'
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.9rem',
                      color: '#666'
                    }
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Sección 2: Configuración de Cotización */}
      <Box sx={{ 
        mb: 4, 
        p: 3, 
        backgroundColor: '#f8f9fa', 
        borderRadius: 3, 
        border: '1px solid #e9ecef' 
      }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3, 
            color: '#2c3e50', 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '"⚙️"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Configuración de Cotización
        </Typography>
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select fullWidth label="Tipo de cliente"
              name="client_type" value={form.client_type || 'NUEVO'}
              onChange={handleChange} error={!!errors.client_type}
              disabled={isReadOnly}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: '#fff', '& .MuiInputLabel-root': { overflow: 'visible', whiteSpace: 'nowrap' } }}
            >
              <MenuItem value="NUEVO">Nuevo</MenuItem>
              <MenuItem value="RECURRENTE">Recurrente</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              select fullWidth label="Prioridad"
              name="priority" value={form.priority || 1}
              onChange={handleChange} error={!!errors.priority}
              disabled={isReadOnly}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: '#fff', '& .MuiInputLabel-root': { overflow: 'visible', whiteSpace: 'nowrap' } }}
            >
              <MenuItem value={1}>Normal</MenuItem>
              <MenuItem value={4}>Urgente</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              select fullWidth label="Tipo de servicio *"
              name="service_type" value={form.service_type || ''}
              onChange={handleChange} error={!!errors.service_type}
              disabled={isReadOnly}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: '#fff', '& .MuiInputLabel-root': { overflow: 'visible', whiteSpace: 'nowrap' } }}
            >
              <MenuItem value="PROTECCION_CIVIL">Protección Civil</MenuItem>
              <MenuItem value="CURSO">Curso</MenuItem>
              <MenuItem value="OTRO">Otro</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              select fullWidth label="Grupo de atención *"
              name="attention_group" value={form.attention_group || ''}
              onChange={handleChange} error={!!errors.attention_group}
              disabled={isReadOnly}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: '#fff', '& .MuiInputLabel-root': { overflow: 'visible', whiteSpace: 'nowrap' } }}
            >
              <MenuItem value="VERACRUZ">Veracruz</MenuItem>
              <MenuItem value="CDMX">CDMX</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        <TextField
          fullWidth multiline rows={2} size="small"
          label="Comentarios adicionales"
          name="comments" value={form.comments || ''}
          onChange={handleChange}
          disabled={isReadOnly}
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: LIMITS.comments }}
          helperText={charCounter(form.comments, LIMITS.comments)}
          FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
          placeholder="Observaciones o notas..."
          sx={{ backgroundColor: '#fff', mt: 2.5 }}
        />
      </Box>

      {/* Sección 3: Selección de Entes (Matriz + Sucursales) */}
      {selectedCompany && (
        <Box sx={{ mb: 4, p: 3, backgroundColor: '#f8f9fa', borderRadius: 3, border: '1px solid #e9ecef' }}>
          <Typography variant="h5" sx={{ mb: 3, color: '#2c3e50', fontWeight: 700, display: 'flex', alignItems: 'center', '&::before': { content: '"🏢"', marginRight: 2, fontSize: '1.2em' } }}>
            Alcance de la Cotización
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Seleccione los entes (matriz y/o sucursales) para los cuales desea cotizar servicios
          </Typography>

          <Grid container spacing={2}>
            {/* Matriz */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                sx={{
                  p: 2,
                  cursor: isReadOnly ? 'default' : 'pointer',
                  border: selectedEntities.some(e => e.id === 'MATRIZ') ? '2px solid #1976d2' : '1px solid #e0e0e0',
                  backgroundColor: selectedEntities.some(e => e.id === 'MATRIZ') ? '#e3f2fd' : '#fff',
                  '&:hover': { boxShadow: isReadOnly ? 0 : 2 },
                  transition: 'all 0.2s'
                }}
                onClick={isReadOnly ? undefined : () => handleEntityToggle({ 
                  id: 'MATRIZ', 
                  name: selectedCompany.businessName, 
                  type: 'MATRIZ',
                  employeesCount: selectedCompany.employeesCount,
                  constructionArea: selectedCompany.constructionArea,
                  landArea: selectedCompany.landArea,
                  constructionLevels: selectedCompany.constructionLevels,
                  address: selectedCompany.propertyAddress,
                  street: selectedCompany.street,
                  exteriorNumber: selectedCompany.exteriorNumber,
                  neighborhood: selectedCompany.neighborhood,
                  city: selectedCompany.city,
                  state: selectedCompany.state,
                  postalCode: selectedCompany.postalCode,
                  isMatrix: true
                })}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox checked={selectedEntities.some(e => e.id === 'MATRIZ')} disabled={isReadOnly} sx={{ mr: 1 }} />
                  <BusinessIcon sx={{ mr: 1, color: '#1976d2' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {selectedCompany.businessName}
                    </Typography>
                    <Chip label="MATRIZ" size="small" color="primary" sx={{ mt: 0.5 }} />
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* Sucursales */}
            {companyBranches.map(branch => (
              <Grid size={{ xs: 12, md: 6 }} key={branch.id}>
                <Paper
                  sx={{
                    p: 2,
                    cursor: isReadOnly ? 'default' : 'pointer',
                    border: selectedEntities.some(e => e.id === branch.id) ? '2px solid #2e7d32' : '1px solid #e0e0e0',
                    backgroundColor: selectedEntities.some(e => e.id === branch.id) ? '#e8f5e9' : '#fff',
                    '&:hover': { boxShadow: isReadOnly ? 0 : 2 },
                    transition: 'all 0.2s'
                  }}
                  onClick={isReadOnly ? undefined : () => handleEntityToggle({ 
                    id: branch.id, 
                    branchName: branch.branchName, 
                    type: 'SUCURSAL',
                    employeesCount: branch.employeesCount,
                    constructionArea: branch.constructionArea,
                    landArea: branch.landArea,
                    constructionLevels: branch.constructionLevels,
                    address: branch.address,
                    street: branch.street,
                    exteriorNumber: branch.exteriorNumber,
                    neighborhood: branch.neighborhood,
                    city: branch.city,
                    state: branch.state,
                    postalCode: branch.postalCode,
                    isMatrix: false
                  })}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox checked={selectedEntities.some(e => e.id === branch.id)} disabled={isReadOnly} sx={{ mr: 1 }} />
                    <StoreIcon sx={{ mr: 1, color: '#2e7d32' }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {branch.branchName}
                      </Typography>
                      <Chip label="SUCURSAL" size="small" color="success" sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Sección 4: Configuración por Ente (Accordions Desplegables) */}
      {selectedEntities.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Typography variant="h5" sx={{ color: '#2c3e50', fontWeight: 700 }}>
              Configuración por Ente
            </Typography>
            <Chip
              label={`${selectedEntities.length} ente${selectedEntities.length > 1 ? 's' : ''}`}
              size="small"
              sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
            />
          </Box>

          {selectedEntities.map((entity, entityIndex) => {
            const entityForm = getEntityForm(entity.id);
            const isExpanded = expandedEntity === entity.id;
            const isMatriz = entity.type === 'MATRIZ';
            const accentColor = isMatriz ? '#1565c0' : '#2e7d32';
            const accentLight = isMatriz ? '#e3f2fd' : '#e8f5e9';
            const serviceCount = (entity.selectedServices || []).length;
            const substanceCount = (entity.hazardousSubstancesList || []).length;
            const riskInfo = getEntityRiskInfo(entity);

            return (
              <Accordion
                key={entity.id}
                expanded={isExpanded}
                onChange={() => setExpandedEntity(isExpanded ? null : entity.id)}
                disableGutters
                sx={{
                  mb: 2,
                  borderRadius: '12px !important',
                  overflow: 'hidden',
                  border: `1px solid ${isExpanded ? accentColor : '#e0e0e0'}`,
                  boxShadow: isExpanded ? `0 4px 20px ${accentColor}22` : '0 1px 4px rgba(0,0,0,0.08)',
                  transition: 'box-shadow 0.2s, border-color 0.2s',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                  sx={{
                    px: 2.5,
                    py: 1,
                    backgroundColor: isExpanded ? accentLight : '#fff',
                    borderLeft: `5px solid ${accentColor}`,
                    transition: 'background-color 0.2s',
                    '& .MuiAccordionSummary-content': { my: 1.5 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: accentColor, color: '#fff',
                      }}
                    >
                      {isMatriz ? <BusinessIcon sx={{ fontSize: 20 }} /> : <StoreIcon sx={{ fontSize: 20 }} />}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e', lineHeight: 1.3 }}>
                          {entity.name}
                        </Typography>
                        <Chip
                          label={isMatriz ? 'Matriz' : 'Sucursal'}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.68rem', fontWeight: 700,
                            backgroundColor: accentColor, color: '#fff',
                          }}
                        />
                      </Box>
                      {(entity.address || entity.street) && (
                        <Typography variant="caption" sx={{ color: '#757575', display: 'block', mt: 0.3 }}>
                          📍 {entity.address || `${entity.street || ''} ${entity.exteriorNumber || ''}, ${entity.neighborhood || ''}, ${entity.city || ''}`}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, mr: 1 }}>
                      <Chip
                        icon={<span style={{ fontSize: '0.8rem', marginLeft: 6 }}>🛠️</span>}
                        label={`${serviceCount} servicio${serviceCount !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          backgroundColor: serviceCount > 0 ? '#e8f5e9' : '#f5f5f5',
                          color: serviceCount > 0 ? '#2e7d32' : '#9e9e9e',
                          fontWeight: 600, fontSize: '0.72rem',
                          border: `1px solid ${serviceCount > 0 ? '#a5d6a7' : '#e0e0e0'}`,
                        }}
                      />
                      <Chip
                        icon={<span style={{ fontSize: '0.8rem', marginLeft: 6 }}>⚗️</span>}
                        label={`${substanceCount} sust.`}
                        size="small"
                        sx={{
                          backgroundColor: substanceCount > 0 ? '#fff3e0' : '#f5f5f5',
                          color: substanceCount > 0 ? '#e65100' : '#9e9e9e',
                          fontWeight: 600, fontSize: '0.72rem',
                          border: `1px solid ${substanceCount > 0 ? '#ffcc80' : '#e0e0e0'}`,
                        }}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <Box sx={{ borderLeft: `5px solid ${accentColor}` }}>

                    {/* ── Sección: Datos del Inmueble ── */}
                    <Box sx={{ px: 3, py: 2.5 }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1, mb: 2,
                        pb: 1, borderBottom: '2px solid #f0f0f0'
                      }}>
                        <Box sx={{ width: 4, height: 20, borderRadius: 2, backgroundColor: '#1976d2' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#37474f', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Datos del Inmueble
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', color: '#607d8b', mt: -1, mb: 1.5 }}>
                        Se toman de {entity.isMatrix ? 'la empresa' : 'la sucursal'}; si los cambias aquí, se actualizan allá al guardar la cotización.
                      </Typography>
                      <Grid container spacing={2}>
                        {[
                          { label: 'Empleados', icon: '👥', field: 'employees_count', value: entity.employees_count },
                          { label: 'M² Construcción', icon: '🏗️', field: 'construction_area', value: entity.construction_area },
                          { label: 'M² Terreno', icon: '🌳', field: 'land_area', value: entity.land_area },
                          { label: 'Niveles', icon: '🏢', field: 'construction_levels', value: entity.construction_levels },
                        ].map(({ label, icon, field, value }) => {
                          const fieldError = getEntityFieldError(field, value);
                          return (
                            <Grid size={{ xs: 6, md: 3 }} key={field}>
                              <Box sx={{
                                p: 1.5, backgroundColor: fieldError ? '#fff3e0' : '#fafafa', borderRadius: 2,
                                border: fieldError ? '1px solid #ed6c02' : '1px solid #e8e8e8',
                                '&:hover': { borderColor: '#1976d2', backgroundColor: '#f0f7ff' },
                                transition: 'all 0.15s',
                              }}>
                                <Typography variant="caption" sx={{ color: fieldError ? '#e65100' : '#78909c', display: 'block', mb: 0.5, fontWeight: 600 }}>
                                  {icon} {label}
                                </Typography>
                                <TextField
                                  fullWidth size="small" type="text"
                                  value={value || ''}
                                  onChange={(e) => updateEntityField(entity.id, field, e.target.value)}
                                  disabled={isReadOnly}
                                  placeholder="—"
                                  variant="standard"
                                  error={!!fieldError}
                                  sx={{
                                    '& .MuiInput-root': { fontSize: '1rem', fontWeight: 700, color: fieldError ? '#e65100' : '#1a1a2e' },
                                    '& .MuiInput-underline:before': { borderBottomColor: 'transparent' },
                                    '& input': { textAlign: 'center', p: 0 },
                                  }}
                                />
                                {fieldError && (
                                  <Typography variant="caption" sx={{ color: '#e65100', display: 'block', mt: 0.3, fontSize: '0.65rem', lineHeight: 1.2 }}>
                                    {fieldError}
                                  </Typography>
                                )}
                              </Box>
                            </Grid>
                          );
                        })}
                        <Grid size={{ xs: 6, md: 3 }} key="risk_level">
                          <Box sx={{
                            p: 1.5, backgroundColor: riskInfo.conflict ? '#fff3e0' : '#fafafa', borderRadius: 2,
                            border: riskInfo.conflict ? '2px solid #ed6c02' : '1px solid #e8e8e8',
                            transition: 'all 0.15s',
                          }}>
                            <Typography variant="caption" sx={{ color: riskInfo.conflict ? '#e65100' : '#78909c', display: 'block', mb: 0.5, fontWeight: 600 }}>
                              ⚠️ Nivel de Riesgo
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 700, color: riskInfo.value ? '#1a1a2e' : '#9e9e9e' }}>
                              {riskInfo.value ? RISK_LEVEL_LABELS[riskInfo.value] : 'Sin definir'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#9e9e9e', display: 'block' }}>
                              Definido por el/los servicio(s) contratado(s)
                            </Typography>
                          </Box>
                        </Grid>
                        {riskInfo.conflict && (
                          <Grid size={{ xs: 12 }}>
                            <Alert
                              severity="warning"
                              icon={<WarningIcon fontSize="inherit" />}
                              sx={{ fontWeight: 700, border: '2px solid #ed6c02', borderRadius: 2, '& .MuiAlert-message': { fontWeight: 700 } }}
                            >
                              Los servicios de este ente tienen niveles de riesgo distintos ({riskInfo.levels.map(l => RISK_LEVEL_LABELS[l]).join(', ')}). Se usará el más alto ({RISK_LEVEL_LABELS[riskInfo.value]}) — revisa los servicios seleccionados de este ente.
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    <Divider />

                    {/* ── Sección: Horario y días laborales ── */}
                    {false && (() => {
                      const schedParts = (entity.workSchedule || '').split('-').map(s => s.trim());
                      const schedStart = schedParts[0] || '';
                      const schedEnd   = schedParts[1] || '';
                      const selectedDays = (entity.workDays || '').split(',').map(d => d.trim()).filter(Boolean);
                      const toggleDay = (dayLabel) => {
                        const next = selectedDays.includes(dayLabel)
                          ? selectedDays.filter(d => d !== dayLabel)
                          : [...selectedDays, dayLabel];
                        updateEntityField(entity.id, 'workDays', next.join(','));
                      };
                      return (
                        <Box sx={{ px: 3, py: 2.5 }}>
                          <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 1, mb: 2,
                            pb: 1, borderBottom: '2px solid #f0f0f0'
                          }}>
                            <Box sx={{ width: 4, height: 20, borderRadius: 2, backgroundColor: '#7b1fa2' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#37474f', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Horario y Días Laborales
                            </Typography>
                          </Box>
                          <Grid container spacing={2} alignItems="flex-start">
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <TextField
                                fullWidth size="small" label="Entrada"
                                type="time" value={schedStart}
                                onChange={(e) => updateEntityField(entity.id, 'workSchedule', `${e.target.value} - ${schedEnd}`)}
                                disabled={isReadOnly}
                                InputLabelProps={{ shrink: true }}
                                sx={{ backgroundColor: '#fafafa', borderRadius: 1 }}
                              />
                            </Grid>
                            <Grid size={{ xs: 6, sm: 3 }}>
                              <TextField
                                fullWidth size="small" label="Salida"
                                type="time" value={schedEnd}
                                onChange={(e) => updateEntityField(entity.id, 'workSchedule', `${schedStart} - ${e.target.value}`)}
                                disabled={isReadOnly}
                                InputLabelProps={{ shrink: true }}
                                sx={{ backgroundColor: '#fafafa', borderRadius: 1 }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="caption" sx={{ color: '#78909c', fontWeight: 600, display: 'block', mb: 1 }}>
                                DÍAS LABORALES
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {[
                                  { label: 'Lun', full: 'Lunes' }, { label: 'Mar', full: 'Martes' },
                                  { label: 'Mié', full: 'Miércoles' }, { label: 'Jue', full: 'Jueves' },
                                  { label: 'Vie', full: 'Viernes' }, { label: 'Sáb', full: 'Sábado' },
                                  { label: 'Dom', full: 'Domingo' },
                                ].map(({ label, full }) => {
                                  const active = selectedDays.includes(full);
                                  return (
                                    <Box
                                      key={full}
                                      onClick={isReadOnly ? undefined : () => toggleDay(full)}
                                      sx={{
                                        px: 1.5, py: 0.5, borderRadius: 2, cursor: isReadOnly ? 'default' : 'pointer',
                                        fontWeight: 700, fontSize: '0.78rem', userSelect: 'none',
                                        border: `1.5px solid ${active ? '#7b1fa2' : '#d0d0d0'}`,
                                        backgroundColor: active ? '#7b1fa2' : '#fff',
                                        color: active ? '#fff' : '#757575',
                                        transition: 'all 0.15s',
                                        '&:hover': isReadOnly ? {} : {
                                          borderColor: '#7b1fa2',
                                          backgroundColor: active ? '#6a1b9a' : '#f3e5f5',
                                          color: active ? '#fff' : '#7b1fa2',
                                        },
                                      }}
                                    >
                                      {label}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      );
                    })()}

                    {/* Dirección Física - Solo para Sucursales */}
                    {entity.type === 'SUCURSAL' && (entity.address || entity.street) && (
                      <>
                        <Divider />
                        <Box sx={{ px: 3, py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ width: 4, height: 20, borderRadius: 2, backgroundColor: '#0288d1' }} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#37474f', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                              Dirección de la Sucursal
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#455a64', pl: 1.5 }}>
                            📍 {entity.address || `${entity.street || ''} ${entity.exteriorNumber || ''}${entity.interiorNumber ? ` Int. ${entity.interiorNumber}` : ''}, ${entity.neighborhood || ''}, ${entity.city || ''}, ${entity.state || ''} ${entity.postalCode || ''}`}
                          </Typography>
                        </Box>
                      </>
                    )}

                    <Divider />

                    {/* ── Sección: Sustancias Peligrosas ── */}
                    <Box sx={{ px: 3, py: 2.5 }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        mb: 2, pb: 1, borderBottom: '2px solid #f0f0f0'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 4, height: 20, borderRadius: 2, backgroundColor: '#e65100' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#37474f', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Sustancias Peligrosas
                          </Typography>
                        </Box>
                        {substanceCount > 0 && (
                          <Chip label={`${substanceCount} registrada${substanceCount !== 1 ? 's' : ''}`} size="small"
                            sx={{ backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #ffcc80' }} />
                        )}
                      </Box>

                      {!isReadOnly && (
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                          <Grid size={{ xs: 12, md: 5 }}>
                            {(() => {
                              const prospectSubstances = [];
                              const sorted = [...hazardousSubstancesCatalog].sort((a, b) => {
                                const aInProspect = prospectSubstances.some(p => p.toLowerCase() === a.name.toLowerCase());
                                const bInProspect = prospectSubstances.some(p => p.toLowerCase() === b.name.toLowerCase());
                                if (aInProspect && !bInProspect) return -1;
                                if (!aInProspect && bInProspect) return 1;
                                return a.name.localeCompare(b.name);
                              });
                              return (
                                <FormControl fullWidth size="small">
                                  <InputLabel>Nombre de la Sustancia</InputLabel>
                                  <Select
                                    value={entityForm.hazardousSubstance.substanceName || ''}
                                    onChange={(e) => updateEntityForm(entity.id, 'hazardousSubstance', {
                                      ...entityForm.hazardousSubstance,
                                      substanceName: e.target.value
                                    })}
                                    label="Nombre de la Sustancia"
                                  >
                                    {sorted.map((substance) => {
                                      const isFromProspect = prospectSubstances.some(
                                        p => p.toLowerCase() === substance.name.toLowerCase()
                                      );
                                      const alreadyAdded = entity.hazardousSubstancesList?.some(
                                        s => s.substanceName === substance.name
                                      );
                                      return (
                                        <MenuItem key={substance.id} value={substance.name}
                                          sx={isFromProspect ? { backgroundColor: '#f1f8e9' } : {}}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Typography sx={{ flex: 1, fontWeight: isFromProspect ? 600 : 400 }}>
                                              {substance.name}
                                            </Typography>
                                            {isFromProspect && (
                                              <Chip label="En Prospecto" size="small"
                                                icon={<BusinessIcon sx={{ fontSize: '0.8rem !important' }} />}
                                                sx={{ backgroundColor: '#4caf50', color: 'white', fontSize: '0.65rem' }} />
                                            )}
                                            {alreadyAdded && (
                                              <Chip label="Agregada" size="small"
                                                sx={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.65rem' }} />
                                            )}
                                            {!isFromProspect && !alreadyAdded && (
                                              <Chip label={substance.category || 'Catálogo'} size="small"
                                                sx={{ backgroundColor: '#1976d2', color: 'white', fontSize: '0.65rem' }} />
                                            )}
                                          </Box>
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </FormControl>
                              );
                            })()}
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField fullWidth size="small" label="Cantidad" type="number"
                              value={entityForm.hazardousSubstance.quantity}
                              onChange={(e) => {
                                const quantity = limitNumberInput(e.target.value, LIMITS.maxQuantity);
                                if (quantity === null) return;
                                updateEntityForm(entity.id, 'hazardousSubstance', { ...entityForm.hazardousSubstance, quantity });
                              }}
                              inputProps={{ min: 0, max: LIMITS.maxQuantity, step: '0.01' }}
                              helperText={`Máx. ${LIMITS.maxQuantity.toLocaleString('es-MX')}`}
                              placeholder="Ej: 500"
                            />
                          </Grid>
                          <Grid size={{ xs: 6, md: 2 }}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Unidad</InputLabel>
                              <Select
                                value={entityForm.hazardousSubstance.unit}
                                onChange={(e) => updateEntityForm(entity.id, 'hazardousSubstance', {
                                  ...entityForm.hazardousSubstance, unit: e.target.value
                                })}
                                label="Unidad"
                              >
                                <MenuItem value="Litros">Litros</MenuItem>
                                <MenuItem value="Kilogramos">Kilogramos</MenuItem>
                                <MenuItem value="Toneladas">Toneladas</MenuItem>
                                <MenuItem value="Galones">Galones</MenuItem>
                                <MenuItem value="Metros cúbicos">Metros cúbicos</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, md: 2 }}>
                            <Button fullWidth variant="contained" size="small"
                              onClick={() => handleAddHazardousSubstanceToEntity(entity.id)}
                              startIcon={<AddCircleIcon />}
                              sx={{ height: '100%', backgroundColor: '#e65100', '&:hover': { backgroundColor: '#bf360c' } }}
                            >
                              Agregar
                            </Button>
                          </Grid>
                        </Grid>
                      )}

                      {(entity.hazardousSubstancesList || []).length > 0 && (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#fff3e0' }}>
                                <TableCell sx={{ fontWeight: 700, color: '#bf360c', fontSize: '0.78rem' }}>Sustancia</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#bf360c', fontSize: '0.78rem' }}>Origen</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#bf360c', fontSize: '0.78rem' }}>Cantidad</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#bf360c', fontSize: '0.78rem' }}>Unidad</TableCell>
                                {!isReadOnly && <TableCell sx={{ fontWeight: 700, color: '#bf360c', fontSize: '0.78rem' }}>Acciones</TableCell>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(entity.hazardousSubstancesList || []).map((substance) => (
                                <TableRow key={substance.id}
                                  sx={{ '&:hover': { backgroundColor: '#fafafa' } }}>
                                  <TableCell>
                                    <Chip label={substance.substanceName} size="small"
                                      sx={{
                                        backgroundColor: hazardousSubstancesCatalog.some(cat => cat.name === substance.substanceName) ? '#1565c0' : '#2e7d32',
                                        color: 'white', fontWeight: 600, fontSize: '0.72rem'
                                      }} />
                                  </TableCell>
                                  <TableCell>
                                    {substance.fromEntity ? (
                                      <Chip label="Del Ente" size="small"
                                        sx={{ backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffcc80', fontWeight: 600, fontSize: '0.7rem' }} />
                                    ) : (
                                      <Chip label="Manual" size="small"
                                        sx={{ backgroundColor: '#f5f5f5', color: '#757575', border: '1px solid #e0e0e0', fontSize: '0.7rem' }} />
                                    )}
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{substance.quantity || '—'}</TableCell>
                                  <TableCell>
                                    <Chip label={substance.unit} size="small" variant="outlined" color="primary" sx={{ fontSize: '0.7rem' }} />
                                  </TableCell>
                                  {!isReadOnly && (
                                    <TableCell>
                                      <IconButton size="small" color="error"
                                        onClick={() => handleRemoveHazardousSubstanceFromEntity(entity.id, substance.id)}>
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>

                    <Divider />

                    {/* ── Sección: Servicios ── */}
                    <Box sx={{ px: 3, py: 2.5 }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        mb: 2, pb: 1, borderBottom: '2px solid #f0f0f0'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 4, height: 20, borderRadius: 2, backgroundColor: '#1976d2' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#37474f', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Servicios
                          </Typography>
                        </Box>
                        {serviceCount > 0 && (
                          <Chip label={`${serviceCount} seleccionado${serviceCount !== 1 ? 's' : ''}`} size="small"
                            sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #90caf9' }} />
                        )}
                      </Box>

                      {isReadOnly ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0', minHeight: 48 }}>
                          {serviceCount === 0 ? (
                            <Typography variant="body2" color="text.secondary">Sin servicios seleccionados</Typography>
                          ) : (
                            (entity.selectedServices || []).map((s) => (
                              <Chip key={s.id}
                                label={`${s.serviceName || s.name || s.title} — $${(editablePrices[s.id] !== undefined ? Number(editablePrices[s.id]) : (s.price || 0)).toFixed(2)}`}
                                color="primary" variant="outlined" size="small" sx={{ fontWeight: 600 }} />
                            ))
                          )}
                        </Box>
                      ) : (
                        <ServiceCarousel
                          onServiceSelect={(service) => handleEntityServiceToggle(entity.id, service)}
                          selectedServices={entity.selectedServices || []}
                          compact={true}
                        />
                      )}
                    </Box>

                    <Divider />

                    {/* ── Sección: Servicios Adicionales ── */}
                    <Box sx={{ px: 3, py: 2.5 }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        mb: 2, pb: 1, borderBottom: '2px solid #f0f0f0'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 4, height: 20, borderRadius: 2, backgroundColor: '#00695c' }} />
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#37474f', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Servicios Adicionales
                          </Typography>
                        </Box>
                        {(entity.additionalServices || []).length > 0 && (
                          <Chip label={`${(entity.additionalServices || []).length} adicional${(entity.additionalServices || []).length !== 1 ? 'es' : ''}`} size="small"
                            sx={{ backgroundColor: '#e0f2f1', color: '#00695c', fontWeight: 600, fontSize: '0.7rem', border: '1px solid #80cbc4' }} />
                        )}
                      </Box>

                      {!isReadOnly && (
                        <Grid container spacing={1.5} sx={{ mb: 2 }}>
                          <Grid size={{ xs: 12, md: 5 }}>
                            <TextField fullWidth size="small" label="Descripción del Servicio"
                              value={entityForm.additionalService.serviceDescription}
                              onChange={(e) => updateEntityForm(entity.id, 'additionalService', {
                                ...entityForm.additionalService, serviceDescription: e.target.value
                              })}
                              inputProps={{ maxLength: LIMITS.additionalDescription }}
                              helperText={charCounter(entityForm.additionalService.serviceDescription, LIMITS.additionalDescription)}
                              FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, md: 3 }}>
                            <TextField fullWidth size="small" label="Precio" type="number"
                              value={entityForm.additionalService.unitPrice}
                              onChange={(e) => {
                                const unitPrice = limitNumberInput(e.target.value, LIMITS.maxPrice);
                                if (unitPrice === null) return;
                                updateEntityForm(entity.id, 'additionalService', { ...entityForm.additionalService, unitPrice });
                              }}
                              inputProps={{ min: 0, max: LIMITS.maxPrice, step: '0.01' }}
                              helperText={`Máx. $${LIMITS.maxPrice.toLocaleString('es-MX')}`}
                              InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
                            />
                          </Grid>
                          <Grid size={{ xs: 6, md: 2 }}>
                            <TextField fullWidth size="small" label="Notas"
                              value={entityForm.additionalService.notes}
                              onChange={(e) => updateEntityForm(entity.id, 'additionalService', {
                                ...entityForm.additionalService, notes: e.target.value
                              })}
                              inputProps={{ maxLength: LIMITS.additionalNotes }}
                              helperText={charCounter(entityForm.additionalService.notes, LIMITS.additionalNotes)}
                              FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 2 }}>
                            <Button fullWidth variant="contained" size="small"
                              onClick={() => handleAddAdditionalServiceToEntity(entity.id)}
                              startIcon={<AddCircleIcon />}
                              sx={{ height: '100%', backgroundColor: '#00695c', '&:hover': { backgroundColor: '#004d40' } }}
                            >
                              Agregar
                            </Button>
                          </Grid>
                        </Grid>
                      )}

                      {(entity.additionalServices || []).length > 0 && (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#e0f2f1' }}>
                                <TableCell sx={{ fontWeight: 700, color: '#004d40', fontSize: '0.78rem' }}>Descripción</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#004d40', fontSize: '0.78rem' }}>Precio</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#004d40', fontSize: '0.78rem' }}>Notas</TableCell>
                                {!isReadOnly && <TableCell sx={{ fontWeight: 700, color: '#004d40', fontSize: '0.78rem' }}>Acciones</TableCell>}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(entity.additionalServices || []).map((service) => {
                                const isRecargo = Boolean(service._isSubstanceRecargo);
                                return (
                                  <TableRow key={service.id}
                                    sx={{ backgroundColor: isRecargo ? '#fff3e0' : 'transparent', '&:hover': { backgroundColor: isRecargo ? '#ffe0b2' : '#fafafa' } }}>
                                    <TableCell sx={{ fontWeight: isRecargo ? 700 : 500, color: isRecargo ? '#e65100' : 'inherit' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {isRecargo && <WarningIcon sx={{ fontSize: 14, color: '#e65100' }} />}
                                        <Box>
                                          {service.serviceDescription}
                                          {isRecargo && service.notes && (
                                            <Typography variant="caption" sx={{ display: 'block', color: '#ef6c00', fontStyle: 'italic' }}>
                                              {service.notes}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: isRecargo ? '#e65100' : '#00695c' }}>${parseFloat(service.unitPrice).toFixed(2)}</TableCell>
                                    <TableCell sx={{ color: '#757575' }}>{isRecargo ? '(se elimina al quitar la sustancia)' : (service.notes || '—')}</TableCell>
                                    {!isReadOnly && (
                                      <TableCell>
                                        {!isRecargo && (
                                          <IconButton size="small" color="error"
                                            onClick={() => handleRemoveAdditionalServiceFromEntity(entity.id, service.id)}>
                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                          </IconButton>
                                        )}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>

                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
      {/* Sección 5: Resumen de Totales por Ente */}
      {selectedEntities.length > 0 && (
        <Box sx={{ mb: 4, p: 3, backgroundColor: '#f8f9fa', borderRadius: 3, border: '1px solid #e9ecef' }}>
          <Typography variant="h5" sx={{ mb: 3, color: '#2c3e50', fontWeight: 700 }}>
            💰 Resumen de Totales
          </Typography>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Ente</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Servicios Principales</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Servicios Adicionales</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedEntities.map((entity) => {
                  const servicesTotal = (entity.selectedServices || []).reduce((sum, s) => {
                    const price = editablePrices[s.id] !== undefined ? Number(editablePrices[s.id]) : (s.price || 0);
                    return sum + price;
                  }, 0);
                  const additionalTotal = (entity.additionalServices || []).reduce((sum, s) => sum + (parseFloat(s.unitPrice) || 0), 0);
                  const entityTotal = servicesTotal + additionalTotal;
                  const hasAdditional = (entity.additionalServices || []).length > 0;

                  return (
                    <React.Fragment key={entity.id}>
                      <TableRow sx={{ borderBottom: hasAdditional ? 'none' : undefined }}>
                        <TableCell sx={{ fontWeight: 600 }}>{entity.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={entity.type} 
                            size="small" 
                            color={entity.type === 'MATRIZ' ? 'primary' : 'success'}
                          />
                        </TableCell>
                        <TableCell align="right">${servicesTotal.toFixed(2)}</TableCell>
                        <TableCell align="right">{hasAdditional ? `$${additionalTotal.toFixed(2)}` : '—'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>${entityTotal.toFixed(2)}</TableCell>
                      </TableRow>
                      {(entity.selectedServices || []).map((svc) => {
                        const servicePrice = editablePrices[svc.id] !== undefined
                          ? Number(editablePrices[svc.id])
                          : (typeof svc.price === 'number' ? svc.price : 0);
                        const minServicePrice = minimumServicePrices[svc.id] !== undefined
                          ? Number(minimumServicePrices[svc.id])
                          : (typeof svc.price === 'number' ? svc.price : 0);

                        return (
                          <TableRow key={`main-service-${entity.id}-${svc.id}`} sx={{ backgroundColor: '#f3f8ff' }}>
                            <TableCell sx={{ pl: 4, color: '#1e4f8a', fontSize: '0.85rem' }}>
                              ↳ {svc.serviceName || svc.name || svc.title || 'Servicio principal'}
                            </TableCell>
                            <TableCell>
                              <Chip label="PRINCIPAL" size="small" sx={{ backgroundColor: '#1976d2', color: 'white', fontSize: '0.7rem' }} />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={servicePrice}
                                onChange={(e) => {
                                  const price = limitNumberInput(e.target.value, LIMITS.maxPrice);
                                  if (price === null) return;
                                  handleEditableServicePriceChange(svc.id, price);
                                }}
                                disabled={isReadOnly}
                                inputProps={{ min: minServicePrice, max: LIMITS.maxPrice, step: '0.01' }}
                                InputProps={{
                                  startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                                  sx: { '& input': { textAlign: 'right', py: 0.7 } }
                                }}
                                helperText={`Monto mínimo: $${minServicePrice.toFixed(2)}`}
                                FormHelperTextProps={{ sx: { color: '#ef6c00', fontWeight: 600 } }}
                                sx={{ width: 130 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#9e9e9e', fontSize: '0.8rem' }}>—</TableCell>
                            <TableCell align="right" sx={{ color: '#1e4f8a', fontWeight: 600 }}>${servicePrice.toFixed(2)}</TableCell>
                          </TableRow>
                        );
                      })}
                      {(entity.additionalServices || []).map((addSvc) => {
                        const isRecargo = Boolean(addSvc._isSubstanceRecargo);
                        return (
                          <TableRow key={addSvc.id} sx={{ backgroundColor: isRecargo ? '#fff8e1' : '#fffde7' }}>
                            <TableCell sx={{ pl: 4, color: isRecargo ? '#e65100' : '#795548', fontSize: '0.85rem' }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                {isRecargo && <WarningIcon sx={{ fontSize: 14, color: '#e65100', mt: '2px', flexShrink: 0 }} />}
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: isRecargo ? 700 : 400, color: 'inherit' }}>
                                    {addSvc.serviceDescription}
                                  </Typography>
                                  {addSvc.notes && (
                                    <Typography variant="caption" sx={{ display: 'block', color: isRecargo ? '#ef6c00' : '#9e9e9e', fontStyle: 'italic' }}>
                                      {addSvc.notes}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {isRecargo
                                ? <Chip icon={<WarningIcon sx={{ fontSize: '0.75rem !important' }} />} label="RECARGO" size="small" sx={{ backgroundColor: '#e65100', color: 'white', fontSize: '0.7rem' }} />
                                : <Chip label="ADICIONAL" size="small" sx={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.7rem' }} />}
                            </TableCell>
                            <TableCell align="right" sx={{ color: '#9e9e9e', fontSize: '0.85rem' }}>—</TableCell>
                            <TableCell align="right" sx={{ color: isRecargo ? '#e65100' : '#795548', fontWeight: isRecargo ? 700 : 400, fontSize: '0.85rem' }}>${(parseFloat(addSvc.unitPrice) || 0).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ color: isRecargo ? '#e65100' : '#795548', fontWeight: isRecargo ? 700 : 400, fontSize: '0.85rem' }}>${(parseFloat(addSvc.unitPrice) || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {additionalServices.length > 0 && (
                  <TableRow sx={{ backgroundColor: '#fff8e1' }}>
                    <TableCell sx={{ fontStyle: 'italic', color: '#f57c00' }}>Servicios Adicionales (generales)</TableCell>
                    <TableCell><Chip label="GENERAL" size="small" sx={{ backgroundColor: '#ff9800', color: 'white' }} /></TableCell>
                    <TableCell align="right">—</TableCell>
                    <TableCell align="right">
                      ${additionalServices.reduce((sum, s) => sum + (parseFloat(s.unitPrice) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      ${additionalServices.reduce((sum, s) => sum + (parseFloat(s.unitPrice) || 0), 0).toFixed(2)}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    TOTAL GENERAL
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#1976d2' }}>
                    ${formatMoney(quotationSummaryTotal(selectedEntities, additionalServices, editablePrices))}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold' }}>
                    IVA ({IVA_RATE_PERCENT}%)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    ${calculateIva(quotationSummaryTotal(selectedEntities, additionalServices, editablePrices)).iva.toFixed(2)}
                  </TableCell>
                </TableRow>
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell colSpan={4} sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    TOTAL CON IVA
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.2rem', color: '#1976d2' }}>
                    ${calculateIva(quotationSummaryTotal(selectedEntities, additionalServices, editablePrices)).totalWithIva.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {false && <Box sx={{ display: 'none' }}>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 3, 
            color: '#2c3e50', 
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            '&::before': {
              content: '"📝"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Información Adicional
        </Typography>
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField 
              label="Número de empleados" 
              name="employees_count" 
              type="text" 
              value={form.employees_count} 
              onChange={handleChange} 
              error={!!errors.employees_count} 
              helperText={errors.employees_count || "Máximo 5 dígitos"} 
              required 
              fullWidth 
              variant="outlined"
              size="small"
              placeholder="Ej: 150"
              sx={{ 
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 1
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  color: '#666'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField 
              label="Metros cuadrados de construcción" 
              name="construction_area" 
              type="text" 
              value={form.construction_area} 
              onChange={handleChange} 
              error={!!errors.construction_area} 
              helperText={errors.construction_area || "Máximo 5 dígitos"} 
              required 
              fullWidth 
              variant="outlined"
              size="small"
              placeholder="Ej: 2500"
              sx={{ 
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 1
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  color: '#666'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField 
              label="Metros cuadrados de terreno" 
              name="land_area" 
              type="text" 
              value={form.land_area} 
              onChange={handleChange} 
              error={!!errors.land_area} 
              helperText={errors.land_area || "Máximo 5 dígitos"} 
              required 
              fullWidth 
              variant="outlined"
              size="small"
              placeholder="Ej: 3000"
              sx={{ 
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 1
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  color: '#666'
                }
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField 
              label="Niveles de construcción" 
              name="construction_levels" 
              type="text" 
              value={form.construction_levels} 
              onChange={handleChange} 
              error={!!errors.construction_levels} 
              helperText={errors.construction_levels || "Máximo 2 dígitos"}
              placeholder="Ej: 3" 
              required 
              fullWidth 
              variant="outlined"
              size="small"
              sx={{ 
                backgroundColor: '#fff',
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.95rem',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 1
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976d2',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.9rem',
                  color: '#666'
                }
              }}
            />
          </Grid>
          
          {/* ELIMINADO: Sección general de Sustancias Peligrosas - ahora se manejan por ente */}
        </Grid>
      </Box>}

      {/* ELIMINADO: Secciones globales de selección de servicios - ahora se manejan por ente */}

      {/* ELIMINADO: Desglose de precios por servicio y entes */}
      {false && ((selectedServices && selectedServices.length > 0) || (additionalServices && additionalServices.length > 0)) && (
        <Box sx={{ 
          mt: 3, 
          p: 2, 
          borderRadius: 2, 
          border: '1px dashed #ced4da', 
          backgroundColor: '#fdfdfd' 
        }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Resumen de precios por servicio y número de entes
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Número de entes: <strong>{calculatePreviewTotals().entities}</strong>
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Servicio</TableCell>
                  <TableCell align="right">Precio unitario</TableCell>
                  <TableCell align="right">Precio × entes ({calculatePreviewTotals().entities})</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Servicios principales */}
                {selectedServices.map((service) => {
                  const unit = editablePrices[service.id] !== undefined ? editablePrices[service.id] : (typeof service.price === 'number' ? service.price : 0);
                  const totalForEntities = unit * calculatePreviewTotals().entities;
                  return (
                    <TableRow key={service.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {service.serviceName || service.name || 'Servicio'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={unit}
                          onChange={(e) => handleEditableServicePriceChange(service.id, e.target.value)}
                          size="small"
                          InputProps={{
                            startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                          }}
                          sx={{
                            width: '120px',
                            '& .MuiOutlinedInput-root': {
                              '& input': {
                                textAlign: 'right',
                                padding: '6px 8px'
                              }
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">${totalForEntities.toFixed(2)}</TableCell>
                    </TableRow>
                  );
                })}
                
                {/* Servicios adicionales */}
                {additionalServices.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell colSpan={3} sx={{ 
                        backgroundColor: '#f5f5f5', 
                        fontWeight: 'bold',
                        borderTop: '2px solid #e0e0e0'
                      }}>
                        Servicios Adicionales
                      </TableCell>
                    </TableRow>
                    {additionalServices
                      .filter(service => {
                        // Filtrar servicios principales (no mostrar en esta sección)
                        const isMainService = service.notes && service.notes.includes('Servicio principal');
                        return !isMainService;
                      })
                      .map((service) => {
                        const unit = typeof service.unitPrice === 'number' ? service.unitPrice : 0;
                        return (
                          <TableRow key={`additional-${service.id}`}>
                            <TableCell>{service.serviceDescription}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                value={unit}
                                onChange={(e) => handleEditableAdditionalServicePriceChange(service.id, e.target.value)}
                                size="small"
                                InputProps={{
                                  startAdornment: <Typography sx={{ mr: 0.5 }}>$</Typography>,
                                }}
                                sx={{
                                  width: '120px',
                                  '& .MuiOutlinedInput-root': {
                                    '& input': {
                                      textAlign: 'right',
                                      padding: '6px 8px'
                                    }
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">${unit.toFixed(2)} (fijo)</TableCell>
                          </TableRow>
                        );
                      })
                    }
                  </>
                )}
                
                {/* Fila de total */}
                <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>TOTAL ESTIMADO</TableCell>
                  <TableCell></TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                    ${calculatePreviewTotals().estimatedSubtotal.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Subtotal estimado (servicios × entes + adicionales): <strong>${calculatePreviewTotals().estimatedSubtotal.toFixed(2)}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              * El cálculo definitivo se realiza en el backend
            </Typography>
          </Box>
        </Box>
      )}

      {/* ELIMINADO: Total General - ahora se muestra en Resumen de Totales por Ente */}
      {false && (selectedServices.length > 0 || additionalServices.length > 0) && (
        <Box sx={{ 
          mt: 3, 
          p: 3, 
          backgroundColor: '#e3f2fd', 
          borderRadius: 2,
          border: '2px solid #1976d2',
          display: 'none'
        }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body1" sx={{ color: '#666' }}>
                Servicios Principales (×{calculatePreviewTotals().entities} entes): ${calculatePreviewTotals().mainServicesTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </Typography>
              <Typography variant="body1" sx={{ color: '#666', mt: 1 }}>
                Servicios Adicionales: ${calculatePreviewTotals().additionalServicesTotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                TOTAL: ${calculatePreviewTotals().estimatedSubtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Snackbar para mensajes más evidentes */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ mt: 8 }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            minWidth: '300px',
            fontSize: '1rem',
            fontWeight: 500,
            boxShadow: 3
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {isAcceptedLock && (
        <Box sx={{ mt: 3 }}>
          <Paper elevation={2} sx={{ p: 3, mb: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2.5, color: '#2c3e50', display: 'flex', alignItems: 'center', gap: 1 }}>
              <LockIcon sx={{ fontSize: 18, color: '#9e9e9e' }} /> Estado del flujo de trabajo
            </Typography>
            <Stepper activeStep={quotationWorkOrder ? 3 : 2} alternativeLabel>
              <Step completed><StepLabel>Cotización creada</StepLabel></Step>
              <Step completed><StepLabel>Aceptada</StepLabel></Step>
              <Step completed={!!quotationWorkOrder}>
                <StepLabel>{quotationWorkOrder ? `OT ${quotationWorkOrder}` : 'OT pendiente'}</StepLabel>
              </Step>
              <Step><StepLabel>Programar visita</StepLabel></Step>
            </Stepper>
          </Paper>

          {quotationWorkOrder ? (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, border: '1px solid #c8e6c9', bgcolor: '#f1f8e9' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#2e7d32' }}>
                ✅ Orden de trabajo generada — ¿Qué sigue?
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', mb: 2.5 }}>
                La orden <strong>{quotationWorkOrder}</strong> está pendiente de programar.
                Dirígete a <strong>Agenda de Servicios</strong> para asignar fecha, hora e inspector.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" color="success" endIcon={<OpenInNewIcon />}
                  onClick={() => navigate('/servicios/agenda')} sx={{ fontWeight: 600 }}>
                  📅 Programar en Agenda
                </Button>
                <Button variant="outlined" color="inherit" endIcon={<OpenInNewIcon />}
                  onClick={() => navigate('/servicios/ordenes-trabajo')} sx={{ fontWeight: 600 }}>
                  📋 Ver Órdenes de Trabajo
                </Button>
              </Box>
            </Paper>
          ) : (
            <Alert severity="warning" icon={<LockIcon />} sx={{ borderRadius: 2, fontWeight: 500 }}>
              Esta cotización <strong>no puede modificarse</strong> porque ya fue <strong>aceptada</strong>.
              En cuanto se genere la orden de trabajo podrás programarla desde Agenda.
            </Alert>
          )}
        </Box>
      )}

      {isReadOnly && !isAcceptedLock ? (
        <Paper
          elevation={0}
          sx={{
            mt: 4, p: 3, borderRadius: 3,
            border: '1.5px solid #90caf9', bgcolor: '#f5f9ff',
            display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565c0', mb: 0.3 }}>
              {quotationStatus === 'PENDING' ? 'Cotización enviada al cliente'
                : quotationStatus === 'CANCELLED' ? 'Cotización cancelada'
                : `Cotización ${(QUOTATION_STATUS_CONFIG[quotationStatus] || { label: quotationStatus }).label?.toLowerCase()}`}
            </Typography>
            <Typography variant="body2" sx={{ color: '#555' }}>
              {quotationStatus === 'PENDING'
                ? 'Ya no puede modificarse. Puedes reenviarla tal cual (por ejemplo, si el correo no le llegó) o crear una nueva versión para cambiarla; esta se cancelará.'
                : quotationStatus === 'CANCELLED'
                  ? (quotationIsLatest
                    ? 'Fue cancelada y ya no puede enviarse ni aceptarse. Si el cliente necesita otra propuesta, crea una nueva versión.'
                    : 'Fue reemplazada por una nueva versión y ya no puede enviarse ni aceptarse.')
                  : 'Ya no puede modificarse. Si el cliente necesita otra propuesta, crea una nueva versión.'}
            </Typography>
            {quotationStatus === 'PENDING' && emailRecipient?.recipient && (
              <Typography variant="caption" sx={{ color: '#777', display: 'block', mt: 0.5 }}>
                Se reenviará a: <strong>{emailRecipient.recipient}</strong>
              </Typography>
            )}
          </Box>
          {quotationStatus === 'PENDING' && (
            <Button
              variant="outlined"
              startIcon={sendingQuotation ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              onClick={handleSendQuotation}
              disabled={sendingQuotation || replacing}
              sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', py: 1.1 }}
            >
              {sendingQuotation ? 'Reenviando...' : 'Reenviar sin cambios'}
            </Button>
          )}
          {quotationStatus === 'CANCELLED' && quotationSentAt && (
            <Button
              variant="outlined"
              startIcon={resendingCancelNotice ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              onClick={handleResendCancellationNotice}
              disabled={resendingCancelNotice || replacing}
              sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', py: 1.1 }}
            >
              Reenviar aviso de cancelación
            </Button>
          )}
          {(['PENDING', 'REJECTED', 'EXPIRED'].includes(quotationStatus) || (quotationStatus === 'CANCELLED' && quotationIsLatest)) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setReplaceDialogOpen(true)}
              disabled={sendingQuotation || replacing}
              sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none', py: 1.1 }}
            >
              Crear nueva versión
            </Button>
          )}
        </Paper>
      ) : isReadOnly ? (
        <Paper
          elevation={0}
          sx={{
            mt: 4, p: 3, borderRadius: 3,
            border: '1.5px solid #b71c1c',
            background: 'linear-gradient(135deg, #ffebee 0%, #fff8f8 100%)',
            display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap',
          }}
        >
          <Box
            sx={{
              width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, #c62828 0%, #b71c1c 100%)',
              color: '#fff',
            }}
          >
            <LockIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#b71c1c', mb: 0.3 }}>
              Cotización bloqueada
            </Typography>
            <Typography variant="body2" sx={{ color: '#555' }}>
              {quotationWorkOrder
                ? <>Esta cotización ya generó la orden de trabajo <strong>{quotationWorkOrder}</strong> y no puede modificarse.</>
                : 'Esta cotización fue aceptada y no puede modificarse.'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={() => navigate('/servicios/ordenes-trabajo')}
            sx={{
              backgroundColor: '#b71c1c', borderRadius: 2, fontWeight: 600, textTransform: 'none',
              '&:hover': { backgroundColor: '#7f0000' },
            }}
          >
            Ver órdenes de trabajo
          </Button>
        </Paper>
      ) : (
        <>
        {editQuotationId && (
          <Alert severity="info" sx={{ mt: 4, borderRadius: 2, alignItems: 'center' }}>
            <strong>Guardada como borrador. El cliente aún no la tiene.</strong>{' '}
            El siguiente paso es enviarla{emailRecipient?.recipient ? <> a <strong>{emailRecipient.recipient}</strong></> : ''}.
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: editQuotationId ? 2 : 4 }}>
          {editQuotationId && emailRecipient?.recipient && (
            <Box sx={{ alignSelf: 'center', textAlign: 'right' }}>
              {emailRecipient.redirected && (
                <Typography variant="caption" sx={{ color: '#888', display: 'block' }}>
                  Ambiente de pruebas (correo del cliente: {emailRecipient.clientEmail || 'sin correo'})
                </Typography>
              )}
              {form.email && emailRecipient.clientEmail && form.email.trim().toLowerCase() !== emailRecipient.clientEmail.trim().toLowerCase() && (
                <Typography variant="caption" sx={{ color: '#ed6c02', display: 'block' }}>
                  Guarda la cotización para enviar al correo nuevo ({form.email})
                </Typography>
              )}
            </Box>
          )}
          <Tooltip title={editQuotationId ? '' : 'Guarda la cotización primero para poder enviarla'}>
            <span>
              <Button
                variant={editQuotationId ? 'contained' : 'outlined'}
                color="primary"
                startIcon={sendingQuotation ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                onClick={handleSendQuotation}
                disabled={!editQuotationId || sendingQuotation || loading}
                sx={{ minWidth: 200, borderRadius: 2, fontWeight: 600, textTransform: 'none', py: 1.2 }}
              >
                {sendingQuotation ? 'Enviando...' : 'Enviar Cotización'}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant={editQuotationId ? 'outlined' : 'contained'}
            color="primary"
            startIcon={loading ? null : <SaveIcon />}
            onClick={handleSubmit}
            disabled={loading || sendingQuotation}
            sx={{ minWidth: 200, borderRadius: 2, fontWeight: 600, textTransform: 'none', py: 1.2 }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : editQuotationId ? 'Guardar cambios' : 'Guardar'}
          </Button>
        </Box>
        </>
      )}

      <Dialog
        open={sendPromptOpen}
        onClose={() => setSendPromptOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6, pb: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CheckCircleIcon sx={{ color: '#2e7d32' }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 700, display: 'block', lineHeight: 1.3 }}>
              Cotización guardada como borrador
            </Typography>
            <Typography variant="caption" color="text.secondary">Los cambios quedaron guardados</Typography>
          </Box>
          <IconButton aria-label="Cerrar" onClick={() => setSendPromptOpen(false)} size="small"
            sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, p: 1.5, mb: 2, borderRadius: 2, bgcolor: '#f5f8fc', border: '1px solid #e3eaf3' }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#1565c0', wordBreak: 'break-all' }}>{folio}</Typography>
            <Chip label="Sin enviar" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600, flexShrink: 0 }} />
          </Box>
          <Typography variant="body2" sx={{ color: 'text.primary', mb: emailRecipient?.recipient ? 1.5 : 0 }}>
            La cotización está guardada, pero <strong>aún no se ha enviado</strong> al cliente.
            ¿Quieres enviarla ahora{emailRecipient?.recipient ? '?' : ' a su contacto?'}
          </Typography>
          {emailRecipient?.recipient && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              <MailOutlineIcon fontSize="small" />
              <Typography variant="body2">Se enviará a <strong style={{ color: '#263238' }}>{emailRecipient.recipient}</strong></Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1, bgcolor: '#fafafa', borderTop: '1px solid #eee' }}>
          <Button variant="outlined" color="inherit" onClick={() => setSendPromptOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, color: 'text.secondary', borderColor: '#cfd8dc' }}>
            Guardar sin enviar
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => { setSendPromptOpen(false); handleSendQuotation(); }}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2.5 }}
          >
            Enviar ahora
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={replaceDialogOpen} onClose={() => !replacing && setReplaceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Crear nueva versión</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            Se creará la siguiente versión de <strong>{folio}</strong> como borrador, con todo copiado (entes, servicios, sustancias y puntos de cobertura), para que la ajustes y la envíes.
          </Typography>
          {quotationStatus === 'PENDING' && (
            <Alert severity="warning">
              Esta versión se <strong>cancelará</strong>: su enlace de aceptación dejará de funcionar y el cliente verá que fue reemplazada.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReplaceDialogOpen(false)} disabled={replacing}>Cancelar</Button>
          <Button variant="contained" onClick={handleReplaceQuotation} disabled={replacing}>
            {replacing ? 'Creando...' : 'Crear nueva versión'}
          </Button>
        </DialogActions>
      </Dialog>

      {saveProgressCard}

      {!isAcceptedLock && sendJob && (
        <Paper ref={sendCardRef} variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, borderColor: sendJob.status === 'ERROR' ? '#e57373' : sendJob.status === 'DONE' ? '#81c784' : '#90caf9' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: sendJob.status === 'ERROR' ? '#c62828' : sendJob.status === 'DONE' ? '#2e7d32' : '#1565c0' }}>
              {sendJob.status === 'ERROR'
                ? '❌ No se pudo enviar la cotización'
                : sendJob.status === 'DONE'
                  ? `✅ Correo enviado a ${sendJob.recipient}`
                  : `Paso ${sendJob.stepNumber} de ${sendJob.totalSteps}: ${sendJob.stepLabel}…`}
            </Typography>
            {sendJob.elapsedMs != null && (
              <Typography variant="caption" sx={{ color: '#777', whiteSpace: 'nowrap' }}>
                {(sendJob.elapsedMs / 1000).toFixed(1)} s
              </Typography>
            )}
          </Box>
          <LinearProgress
            variant="determinate"
            value={sendJob.status === 'DONE' ? 100 : sendJob.percent || 0}
            color={sendJob.status === 'ERROR' ? 'error' : sendJob.status === 'DONE' ? 'success' : 'primary'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          {sendJob.status === 'ERROR' && sendJob.error && (
            <Typography variant="caption" sx={{ color: '#c62828', display: 'block', mt: 1 }}>
              {sendJob.error}
            </Typography>
          )}
        </Paper>
      )}

      {/* Modal para agregar nueva sustancia al catálogo */}
      <AddHazardousSubstanceModal
        open={substanceModalOpen}
        onClose={() => setSubstanceModalOpen(false)}
        onSubstanceAdded={handleSubstanceAdded}
      />
    </Box>
  );
};

export default NewQuotation;
